import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { formatNumberWithCommas } from "@app/helpers";
import {
  RootState,
  setIdReport,
  setIsUpdatedMachineReportResponses,
  setShowAlert,
  store,
} from "@app/store";
import {
  getUser,
  apiGetMachineReportResponses,
  apiGetFuelMaintenanceReport,
  apiGetMaintenanceReport,
  apiMachineReportActionChoices,
  apiPostMachineReportResponses,
  apiDeleteMachineReport,
  apiGetMachineReportOriginal,
  getMachinePDF,
  getMachineMaintenancePDF,
  updateMachineReportStatus,
  apiGetReportMachineNameInfo,
  apiGetReportMachineNameInfoMaintenance,
} from "@app/api";
import {
  Button,
  FormControl,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  Switch,
  TextField,
  FormGroup,
  Skeleton,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  PictureAsPdfOutlined,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import {
  FUEL_MAINTENANCE_REPORTS,
  INCIDENT_REPORTS,
  INSPECTION,
  INVALID_URL,
  MAINTENANCE_REPORTS,
  ResponseStatus,
  DialogTypes,
  DevicePlatform,
  UploadFileType,
  STATUS_UPDATES,
  ReportActions,
  TYPE_FILE_UPLOAD,
} from "@app/constants";
import { DataDialog, DataTooltip, Video } from "@app/components/atoms";
import {
  IconActionOverflow,
  IconDelete,
  IconEdit,
  IconInvalidUrl,
  IconUser,
  IconInputError,
  IconWarningFormEdit,
} from "@app/components/icons";
import { useStoreDispatch } from "@app/hooks";
import { useForm } from "react-hook-form";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Mousewheel, Keyboard } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { REGEX, handleUploadStore } from "@app/helpers";
import {
  ConfirmDialog,
  ConfirmDialogProps,
  MediaUploadMachineReportHistory,
} from "@app/pages/Features";
import { CreateRefuel } from "@app/pages/Features/MachineDetail/Components/Report/CreateReports/Refuel/CreateRefuel";
import type { RefuelPrefill } from "@app/pages/Features/MachineDetail/Components/Report/CreateReports/Refuel/CreateRefuel";
import { CreateAbnormality } from "@app/pages/Features/MachineDetail/Components/Report/CreateReports/Abnormality/CreateAbnormality";
import type { AbnormalityPrefill } from "@app/pages/Features/MachineDetail/Components/Report/CreateReports/Abnormality/CreateAbnormality";
import { useSelector } from "react-redux";
import RecordRTC from "recordrtc";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import { IconDeleteRed } from "@app/components/icons/IconDeleteRed/IconDeleteRed";
import StopIcon from "@mui/icons-material/Stop";
import { Machine, Report } from "@app/types";
import CustomImageViewer from "@app/components/atoms/CustomImageViewer/CustomImageViewer";
import PdfFileIcon from "@app/components/icons/PdfFileIcon/PdfFileIcon";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  ReportDialogSignatureFooter,
  ReportLastUpdatedHeader,
  ReportSignatureField,
} from "./ReportSignatureSection";
import React from "react";
import CustomVideoViewer from "@app/components/atoms/CustomVideoViewer/CustomVideoViewer";
import { MaintenanceReportV3Dialog } from "./MaintenanceReportV3Dialog";

type AllReportsDataDialogProps = {
  id: any;
  machineId: any;
  machineReportId: any;
  reportType: any;
  reportVersion?: string | null;
  machineModelType?: string;
  onClose?: () => void;
};

function IncidentReportSkeleton() {
  return (
    <>
      {[0].map((cardIdx) => (
        <Box
          key={cardIdx}
          sx={{
            mb: 2,
            p: 2,
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            backgroundColor: "#fafafa",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <Skeleton
              variant="circular"
              width={40}
              height={40}
              sx={{ mr: 1.5, flexShrink: 0 }}
            />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="50%" height={20} />
              <Skeleton variant="text" width="30%" height={16} />
            </Box>
          </Box>

          <Skeleton variant="text" width="90%" height={18} />
          <Skeleton variant="text" width="75%" height={18} />
          <Skeleton variant="text" width="60%" height={18} sx={{ mb: 1.5 }} />

          <Skeleton
            variant="rectangular"
            width="100%"
            height={180}
            sx={{ borderRadius: 1, mb: 1 }}
          />

          <Box sx={{ mt: 1 }}>
            <Skeleton variant="text" width="35%" height={14} />
            <Skeleton variant="text" width="20%" height={18} />
          </Box>
        </Box>
      ))}
    </>
  );
}

function FuelMaintenanceReportSkeleton() {
  return (
    <>
      {[0, 1].map((cardIdx) => (
        <Box
          key={cardIdx}
          sx={{
            mb: 2,
            p: 2,
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            backgroundColor: "#fafafa",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <Skeleton
              variant="circular"
              width={40}
              height={40}
              sx={{ mr: 1.5, flexShrink: 0 }}
            />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="50%" height={20} />
              <Skeleton variant="text" width="30%" height={16} />
            </Box>
          </Box>

          <Skeleton variant="text" width="40%" height={14} />
          <Skeleton variant="text" width="55%" height={18} sx={{ mb: 1 }} />

          <Skeleton variant="text" width="35%" height={14} />
          <Skeleton variant="text" width="25%" height={18} sx={{ mb: 1 }} />

          <Skeleton
            variant="rectangular"
            width="100%"
            height={160}
            sx={{ borderRadius: 1 }}
          />
        </Box>
      ))}
    </>
  );
}

function MaintenanceReportSkeleton() {
  return (
    <>
      {[0, 1, 2].map((cardIdx) => (
        <Box
          key={cardIdx}
          sx={{
            mb: 2,
            p: 2,
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            backgroundColor: "#fafafa",
          }}
        >
          {cardIdx === 0 && (
            <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
              <Skeleton
                variant="circular"
                width={40}
                height={40}
                sx={{ mr: 1.5, flexShrink: 0 }}
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="50%" height={20} />
                <Skeleton variant="text" width="30%" height={16} />
              </Box>
            </Box>
          )}

          <Skeleton variant="text" width="40%" height={14} />
          <Skeleton variant="text" width="65%" height={18} sx={{ mb: 1 }} />

          <Skeleton variant="text" width="45%" height={14} />
          <Skeleton variant="text" width="50%" height={18} sx={{ mb: 1 }} />

          {cardIdx > 0 && (
            <Skeleton
              variant="rectangular"
              width="100%"
              height={160}
              sx={{ borderRadius: 1, mt: 1 }}
            />
          )}
        </Box>
      ))}
    </>
  );
}

function SubTitleSkeleton({ showControls }: { showControls: boolean }) {
  return (
    <Box sx={{ width: "100%" }}>
      <Skeleton
        variant="text"
        width={160}
        height={18}
        sx={{ mb: showControls ? 1.5 : 0 }}
      />
      {showControls && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Skeleton
            variant="rectangular"
            width={182}
            height={40}
            sx={{ borderRadius: 1 }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Skeleton variant="circular" width={12} height={12} />
            <Skeleton variant="text" width={50} height={18} />
            <Skeleton
              variant="rectangular"
              width={48}
              height={26}
              sx={{ borderRadius: 2 }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export function AllReportsDataDialog({
  id,
  machineId,
  machineReportId,
  reportType,
  reportVersion,
  machineModelType,
  onClose,
}: AllReportsDataDialogProps) {
  const { t } = useTranslation();
  const dispatch = useStoreDispatch();
  const [openFormReport, setOpenFormReport] = useState<string>(INSPECTION);
  const [targetRow, setTargetRow] = useState<any>(null);
  const [isRead, setIsRead] = useState(false);
  const [screenPermission, setScreenPermission] = useState<any>();
  const [machineReportActionChoices, setMachineReportActionChoices] =
    useState<any[]>();
  const [isResolved, setIsResolved] = useState(false);
  const [currentMachineReportResponseId, setCurrentMachineReportResponseId] =
    useState<string>("");
  const [initialDropdownValue, setInitialDropdownValue] = useState("");
  const [isUpdateSectionOpen, setIsUpdateSectionOpen] = useState(false);
  const [mediaUploadErrorMsg, setMediaUploadErrorMsg] = useState("");
  const [, setCurrentMachineReportResponseIds] = useState<string[]>([]);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [isReportCommentInputFocus, setIsReportCommentInputFocus] =
    useState(false);
  const [prevReportComment, setPrevReportComment] = useState("");
  const [isDropdownDisabled, setIsDropdownDisabled] = useState(false);
  const [isSwitchDisabled, setIsSwitchDisabled] = useState(false);
  const [userTimeFormat, setUserTimeFormat] =
    useState<string>("yyyy/MM/dd HH:mm");
  const [localeCode, setLocaleCode] = useState("");
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const [fuelMenuAnchorEl, setFuelMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const fuelOverflowButtonRef = useRef<HTMLSpanElement>(null);
  const [openFuelDelete, setOpenFuelDelete] = useState(false);
  const [isDeletingFuel, setIsDeletingFuel] = useState(false);
  const [openMaintDelete, setOpenMaintDelete] = useState(false);
  const [openEditRefuel, setOpenEditRefuel] = useState(false);
  const [refuelPrefill, setRefuelPrefill] = useState<RefuelPrefill | null>(
    null,
  );
  const [openAbnormalityDelete, setOpenAbnormalityDelete] = useState(false);
  const [isDeletingAbnormality, setIsDeletingAbnormality] = useState(false);
  const [openEditAbnormality, setOpenEditAbnormality] = useState(false);
  const [abnormalityPrefill, setAbnormalityPrefill] =
    useState<AbnormalityPrefill | null>(null);
  const [
    openAbnormalityFromInspectionHint,
    setOpenAbnormalityFromInspectionHint,
  ] = useState(false);
  const [isLoadingAbnormalityEdit, setIsLoadingAbnormalityEdit] =
    useState(false);
  const [abnormalityMenuAnchorEl, setAbnormalityMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [maintMenuAnchorEl, setMaintMenuAnchorEl] =
    useState<HTMLElement | null>(null);
  const maintOverflowButtonRef = useRef<HTMLSpanElement>(null);
  const abnormalityOverflowButtonRef = useRef<HTMLSpanElement>(null);
  const reduxScreenPermission = useSelector(
    (state: RootState) => state.screenPermission.value,
  );
  const effectiveScreenPermission = screenPermission || reduxScreenPermission;
  const [IncidentReportPDFdialogOpen, setIncidentReportPDFdialogOpen] =
    useState(false);
  const [pdfHeaderRemarks, setPdfHeaderRemarks] = useState("");
  const [pdfFooterRemarks, setPdfFooterRemarks] = useState("");
  const [pdfHeaderRemarksError, setPdfHeaderRemarksError] = useState(false);
  const [pdfFooterRemarksError, setPdfFooterRemarksError] = useState(false);
  const [MaintReportPDFdialogOpen, setMaintReportPDFdialogOpen] =
    useState(false);
  const [pdfMaintHeaderRemarks, setMaintPdfHeaderRemarks] = useState("");
  const [pdfMaintFooterRemarks, setMaintPdfFooterRemarks] = useState("");
  const [pdfMaintHeaderRemarksError, setMaintPdfHeaderRemarksError] =
    useState(false);
  const [pdfMaintFooterRemarksError, setMaintPdfFooterRemarksError] =
    useState(false);
  const [openDialogConfirm, setOpenDialogConfirm] = useState(false);
  const updateSectionRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [initialSwitchValue, setInitialSwitchValue] = useState(isResolved);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogProps>({});
  const [openUpdateStatus, setOpenUpdateStatus] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recorder, setRecorder] = useState<any>(null);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaAvailable, setMediaAvailable] = useState(true);
  const recordingStartTimeRef = useRef<number | null>(null);
  const recordingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [recordingInterval, setRecordingInterval] = useState<any>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [existingRecordingDeleted, setExistingRecordingDeleted] =
    useState(false);
  const stoppedRef = useRef(false);
  const [recordingFiles, setRecordingFiles] = useState<RecordingFile[]>([]);
  const [, setRecordingCount] = useState(0);
  const [openFormNoMic, setOpenFormNoMic] = useState(false);
  const [openFormDeleteRecording, setOpenFormDeleteRecording] = useState(false);
  const [open, setOpen] = useState(true);
  const [machineReportsOpen, setMachineReportsOpen] = useState<Machine[]>([]);

  const [pdfConfirmOpen, setPdfConfirmOpen] = useState(false);
  const [pendingDialog, setPendingDialog] = useState<
    "INCIDENT" | "MAINT" | null
  >(null);
  const [openLocation, setOpenLocation] = useState(false);
  const [record, setRecord] = useState<Machine>();

  const [isLoadingIncident, setIsLoadingIncident] = useState(false);
  const [isLoadingFuelMaintenance, setIsLoadingFuelMaintenance] =
    useState(false);
  const [isLoadingMaintenance, setIsLoadingMaintenance] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [videoViewerOpen, setVideoViewerOpen] = useState(false);
  const [isDeletingMaint, setIsDeletingMaint] = useState(false);

  type RecordingFile = {
    fileName: string;
    recordingUrl: string;
    filePath: string;
    mimeType: string;
    recordingAt: string;
  };

  useEffect(() => {
    if (reportType && reportType !== INSPECTION) {
      setOpenFormReport(reportType);
    }
  }, [reportType]);

  const {
    register,
    setValue,
    reset,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      reportComment: "",
      lat: null,
      lng: null,
      locationAccuracy: "string",
      devicePlatform: DevicePlatform.WEB,
      machineReportMedias: [] as File[],
      reportActionChoiceIds: [] as string[],
      status: "",
    },
  });
  const currentMedias = watch("machineReportMedias") || [];

  const handleShowReportAction = (actions: string[]) => {
    return actions?.map((action: any) => {
      const actionCode =
        action?.reportActionChoiceCode || action?.reportActionCode;
      switch (actionCode) {
        case ReportActions.SELF_REPAIR:
          return (
            <span
              key={"selfRepair"}
              className={`label-action label-purple fs-12 ${
                action?.reportActionChoiceCode ? "ml-5" : ""
              } `}
            >
              {t("selfrepair")}
            </span>
          );
        case ReportActions.REPAIR_REQUEST:
          return (
            <span
              key={"repairRequest"}
              className={`label-action label-pink fs-12 ${
                action?.reportActionChoiceCode ? "ml-5" : ""
              } `}
            >
              {t("repairRequest")}
            </span>
          );
        default:
          return null;
      }
    });
  };

  const updateBtnDisableStatus = () => {
    const {
      reportComment,
      machineReportMedias,
      reportActionChoiceIds,
      status,
    } = watch();

    const isDropdownReverted =
      currentMachineReportResponseId === initialDropdownValue;

    const isDefaultOptionSelected =
      reportActionChoiceIds?.length === 0 ||
      currentMachineReportResponseId === "Select";

    const isAnyOtherInputChanged =
      !!reportComment.trim() ||
      !!machineReportMedias?.length ||
      (!!status && status !== targetRow?.reportResponseStatus) ||
      !!recordedBlob ||
      recordingFiles.length > 0;

    return isRecording || isPaused
      ? false
      : (isAnyOtherInputChanged ||
          (!isDefaultOptionSelected && !isDropdownReverted)) &&
          !errors?.reportComment?.type;
  };

  const uploadDescription = [
    `${t("dialogReport.pleaseUploadFile")}`,
    `<span class="fs-12">${t("dialogReport.thatCanBeUploadedAre")}</span>`,
    `<span class="fs-12">${t(
      "dialogReport.fileFormatsMachineReportHistory",
    )}</span>`,
    `<span class="fs-12">${t("dialogReport.videosCanBeUploaded")}</span>`,
  ];

  const getMachineReportResponses = useCallback(
    (groupId: string, machineId: string, machineReportId: string) => {
      setIsLoadingIncident(true);
      return apiGetMachineReportResponses(groupId, machineId, machineReportId)
        .then((res) => {
          const { data, meta } = res.data;
          const { machineReportResponses, reportResponseStatus } = data;

          const lastIndex = machineReportResponses.length - 1;
          const lastItem = machineReportResponses[lastIndex];

          if (lastItem.reportActionChoices.length > 0) {
            const reportActionChoiceId =
              lastItem.reportActionChoices[0].reportActionChoiceId;
            setCurrentMachineReportResponseId(reportActionChoiceId);
            setInitialDropdownValue(reportActionChoiceId);
          } else {
            setCurrentMachineReportResponseId("");
            setInitialDropdownValue("");
          }

          const machineReportResponseIds = machineReportResponses.map(
            (item: any) => item.machineReportResponseId,
          );
          setValue("status", "");
          setIsResolved(reportResponseStatus === ResponseStatus.RESOLVED);
          setOpenFormReport(INCIDENT_REPORTS);
          setIsRead(!data.isRead && data.isRead !== null);

          setTargetRow({
            ...data,
            machineId: machineId,
            machineReportId: machineReportId,
          });
          setScreenPermission(meta.screenPermission);
          setCurrentMachineReportResponseIds(machineReportResponseIds);
        })
        .catch((error) => {
          console.error("Failed to get machine report responses:", error);
        })
        .finally(() => {
          setIsLoadingIncident(false);
        });
    },
    [setValue],
  );

  useEffect(() => {
    if (machineReportId && id && machineId) {
      switch (reportType) {
        case INCIDENT_REPORTS:
          getMachineReportResponses(id, machineId, machineReportId);
          break;
        case FUEL_MAINTENANCE_REPORTS:
          getFuelMaintenanceReport(id, machineId, machineReportId);
          break;
        case MAINTENANCE_REPORTS:
          getMaintenanceReport(id, machineId, machineReportId);
          break;
        default:
          setOpenFormReport(INSPECTION);
      }
    }
  }, [machineReportId, id, machineId, reportType, getMachineReportResponses]);

  const getFuelMaintenanceReport = (
    groupId: string,
    machineId: string,
    machineReportId: string,
  ) => {
    setIsLoadingFuelMaintenance(true);
    return apiGetFuelMaintenanceReport(groupId, machineId, machineReportId)
      .then((res) => {
        const { data, meta } = res.data;
        setOpenFormReport(FUEL_MAINTENANCE_REPORTS);
        setTargetRow({
          ...data,
          machineId: machineId,
          machineReportId: machineReportId,
        });
        if (meta?.screenPermission) {
          setScreenPermission(meta.screenPermission);
        }
      })
      .catch((error) => {
        console.error("Failed to get fuel maintenance report:", error);
      })
      .finally(() => {
        setIsLoadingFuelMaintenance(false);
      });
  };

  const getMaintenanceReport = (
    groupId: string,
    machineId: string,
    machineReportId: string,
  ) => {
    setIsLoadingMaintenance(true);
    return apiGetMaintenanceReport(groupId, machineId, machineReportId)
      .then((res) => {
        const { data } = res.data;
        setOpenFormReport(MAINTENANCE_REPORTS);
        setTargetRow({
          ...data,
          machineId: machineId,
          machineReportId: machineReportId,
        });
      })
      .catch((error) => {
        console.error("Failed to get maintenance report:", error);
      })
      .finally(() => {
        setIsLoadingMaintenance(false);
      });
  };

  const handleCloseDialog = () => {
    setOpenFormReport(INSPECTION);
    setTargetRow(null);
    reset();
    setIsRead(false);
    setIsResolved(false);
    setCurrentMachineReportResponseId("");
    setInitialDropdownValue("");
    setIsUpdateSectionOpen(false);
    setMediaUploadErrorMsg("");
    setCurrentMachineReportResponseIds([]);
    setIsFormChanged(false);
    setIsReportCommentInputFocus(false);
    setPrevReportComment("");
    setIsDropdownDisabled(false);
    setIsSwitchDisabled(false);
    setPdfHeaderRemarks("");
    setPdfFooterRemarks("");
    setPdfHeaderRemarksError(false);
    setPdfFooterRemarksError(false);
    setMaintPdfHeaderRemarks("");
    setMaintPdfFooterRemarks("");
    setMaintPdfHeaderRemarksError(false);
    setMaintPdfFooterRemarksError(false);

    if (isRecording || isPaused) {
      stopRecording();
    }
    setRecordedBlob(null);
    setRecordingTime(0);
    setIsPaused(false);
    setIsRecording(false);
    setRecorder(null);
    setRecordingInterval(null);
    setMediaAvailable(true);
    setRecordingFiles([]);
    setRecordingCount(0);
    setExistingRecordingDeleted(false);

    setIncidentReportPDFdialogOpen(false);
    setMaintReportPDFdialogOpen(false);
    setOpenFormNoMic(false);
    setOpenFormDeleteRecording(false);
    setOpenDialogConfirm(false);

    setIsLoadingIncident(false);
    setIsLoadingFuelMaintenance(false);
    setIsLoadingMaintenance(false);

    dispatch(setIsUpdatedMachineReportResponses(`${new Date()}`));

    if (onClose) {
      onClose();
    }

    setOpen(false);
  };

  const getUserPicture = (
    item: any,
    machineReportResponseIndex?: number,
  ): JSX.Element => {
    const hasUserPictureUrl = item?.pictureUrl;
    const isUserPictureUrlInvalid = item.pictureUrl === INVALID_URL;
    let userPicture = (
      <Box
        sx={{
          width: 40,
          height: 40,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <IconUser size={40} />
      </Box>
    );
    if (hasUserPictureUrl) {
      if (isUserPictureUrlInvalid) {
        userPicture = (
          <Box
            sx={{
              minWidth: 40,
              height: 40,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <IconInvalidUrl />
          </Box>
        );
      } else {
        userPicture = (
          <Avatar
            sx={{
              width: 40,
              height: 40,
            }}
            alt="avatar-reporter"
            src={item.pictureUrl}
            imgProps={{
              onError: () => {
                if (machineReportResponseIndex !== undefined) {
                  const newMachineReportResponses = [
                    ...targetRow.machineReportResponses,
                  ];
                  newMachineReportResponses[
                    machineReportResponseIndex
                  ].pictureUrl = INVALID_URL;
                  const newTargetRow = {
                    ...targetRow,
                    machineReportResponses: newMachineReportResponses,
                  };
                  setTargetRow(newTargetRow);
                } else {
                  const newTargetRow = { ...targetRow };
                  newTargetRow.pictureUrl = INVALID_URL;
                  setTargetRow(newTargetRow);
                }
              },
            }}
          />
        );
      }
    }
    return userPicture;
  };

  const renderMedia = (
    media: any,
    mediaIndex: number,
    machineReportResponseIndex?: number,
    thumbnailCover: boolean = false,
  ) => {
    const isInvalidMediaUrl = media?.mediaUrl === INVALID_URL;
    const isMediaMimeTypeVideo = media?.mimeType?.includes("video");
    const isMediaMimeTypePdf =
      media?.mimeType === "application/pdf" ||
      media?.fileName?.toLowerCase().endsWith(".pdf");

    if (isInvalidMediaUrl) {
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <IconInvalidUrl />
        </Box>
      );
    }

    if (isMediaMimeTypePdf) {
      return null;
    }

    if (!isMediaMimeTypeVideo && !isInvalidMediaUrl) {
      return (
        <Box
          className="swiper-image img-report"
          component="img"
          alt="swiper-image"
          src={media.mediaUrl}
          sx={{
            objectFit: "cover",
          }}
          onError={() => {
            if (machineReportResponseIndex !== undefined) {
              const newMachineReportResponses = [
                ...targetRow.machineReportResponses,
              ];
              newMachineReportResponses[
                machineReportResponseIndex
              ].machineReportMedias[mediaIndex].mediaUrl = INVALID_URL;
              const newTargetRow = {
                ...targetRow,
                machineReportResponses: newMachineReportResponses,
              };
              setTargetRow(newTargetRow);
            } else {
              const newTargetRow = { ...targetRow };
              newTargetRow.machineReportMedias[mediaIndex].mediaUrl =
                INVALID_URL;
              setTargetRow(newTargetRow);
            }
          }}
        />
      );
    }

    return (
      <Video
        media={media}
        thumbnailCover={thumbnailCover}
        onError={() => {
          if (machineReportResponseIndex !== undefined) {
            const newMachineReportResponses = [
              ...targetRow.machineReportResponses,
            ];
            newMachineReportResponses[
              machineReportResponseIndex
            ].machineReportMedias[mediaIndex].mediaUrl = INVALID_URL;
            const newTargetRow = {
              ...targetRow,
              machineReportResponses: newMachineReportResponses,
            };
            setTargetRow(newTargetRow);
          } else {
            const newTargetRow = { ...targetRow };
            newTargetRow.machineReportMedias[mediaIndex].mediaUrl = INVALID_URL;
            setTargetRow(newTargetRow);
          }
        }}
      />
    );
  };

  const handleConvertMedia = async (files: File[]) => {
    const array = [];
    for await (const item of files) {
      const newItem = (await handleUploadStore(
        UploadFileType.MACHINE_REPORT,
        item,
      )) as any;

      array.push(
        newItem
          ? {
              fileName: newItem?.name || newItem?.fileName,
              filePath: newItem?.filePath,
              mediaUrl: newItem?.filePath,
              mimeType: newItem?.type || newItem?.mimeType,
              captureMediaAt: newItem.lastModified
                ? new Date(newItem.lastModified).toISOString()
                : new Date().toISOString(),
            }
          : null,
      );
    }
    return array;
  };

  const onSubmit = async (data: any) => {
    if (id && targetRow.machineId && machineReportId) {
      setMediaUploadErrorMsg("");
      const newMachineReportMedias = await handleConvertMedia(
        data.machineReportMedias || [],
      );
      if (newMachineReportMedias?.some((item) => item === null)) {
        setMediaUploadErrorMsg(t("messages.errConmpressImg"));
        return;
      }
      let machineReportRecordings: any[] = existingRecordingDeleted
        ? []
        : [...recordingFiles];

      if (recordedBlob) {
        const audioFile = new File(
          [recordedBlob],
          `recording_${Date.now()}.webm`,
          { type: "audio/webm" },
        );

        const uploadedAudio = await handleUploadStore(
          UploadFileType.MACHINE_REPORT,
          audioFile,
        );

        if (uploadedAudio) {
          const audioRecording = {
            fileName: uploadedAudio.name || uploadedAudio.fileName,
            filePath: uploadedAudio.filePath,
            recordingUrl: uploadedAudio.filePath,
            mimeType: uploadedAudio.type || uploadedAudio.mimeType,
            recordingAt: new Date().toISOString(),
          };

          machineReportRecordings.push(audioRecording);
        } else {
          console.warn("Audio upload failed.");
        }
      }
      const payload = {
        ...data,
        machineReportMedias: newMachineReportMedias,
        machineReportRecordings,
      };
      if (!data.status) {
        delete payload.status;
      }
      return apiPostMachineReportResponses(
        id ?? "",
        targetRow.machineId,
        machineReportId,
        payload,
      ).then((res) => {
        dispatch(setShowAlert(res?.data?.meta?.successMessage));
        reset({ reportActionChoiceIds: [], reportComment: "" });
        setOpenUpdateStatus(false);

        setIsFormChanged(false);
        resetForm();

        setOpenUpdateStatus(false);
        setIsUpdateSectionOpen(false);
        dispatch(setIsUpdatedMachineReportResponses(`${new Date()}`));

        if (isRecording || isPaused) {
          stopRecording();
        }

        setRecordedBlob(null);
        setRecordingTime(0);
        setIsPaused(false);
        setIsRecording(false);
        setRecorder(null);
        setRecordingInterval(null);
        setMediaAvailable(true);

        setRecordingFiles([]);
        setRecordingCount(0);
        setExistingRecordingDeleted(false);
        setOpen(false);
      });
    }
  };

  const subTitleDialog = (targetRow: any) => {
    if (isLoadingIncident && openFormReport === INCIDENT_REPORTS) {
      return <SubTitleSkeleton showControls />;
    }

    return (
      !!targetRow && (
        <div className="sub-title-dialog" style={{ width: "100%" }}>
          <>
            {targetRow?.reportedAt &&
            !isNaN(new Date(targetRow.reportedAt).getTime())
              ? format(new Date(targetRow.reportedAt), userTimeFormat)
              : ""}

            {openFormReport === INCIDENT_REPORTS && (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",

                    justifyContent: "space-between",
                    width: "100%",
                    flexWrap: "wrap",
                    gap: "10px",
                    marginTop: "10px",
                  }}
                >
                  <FormControl style={{ minWidth: 182, maxWidth: 182 }}>
                    <TextField
                      select
                      label={t("dialogReport.repairAction")}
                      value={currentMachineReportResponseId}
                      onChange={handleOnChangeReportActionChoice}
                      variant="outlined"
                      fullWidth
                      size="small"
                      disabled={isDropdownDisabled}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            style: {
                              width: "auto",
                              minWidth: 182,
                              maxWidth: 182,
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="Select">
                        <em>{t("NotSelect")}</em>
                      </MenuItem>
                      {machineReportActionChoices?.map((choice) => (
                        <MenuItem
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                            display: "block",
                          }}
                          key={choice.reportActionChoiceId}
                          value={choice.reportActionChoiceId}
                        >
                          <DataTooltip isTextPre width="100%">
                            {choice.reportActionChoiceName}
                          </DataTooltip>
                        </MenuItem>
                      ))}
                    </TextField>
                  </FormControl>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      flexDirection: "row",
                      width: "auto",
                    }}
                    className="switch-container"
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      <span
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: isResolved ? "#4CAF50" : "#B0B0B0",
                          display: "inline-block",
                        }}
                      ></span>

                      <Typography variant="body2">
                        {isResolved ? t("resolved") : t("open")}
                      </Typography>
                    </div>

                    <Switch
                      className={`btn-switch ${isResolved && "checked"} `}
                      inputProps={{ "aria-label": "Switch A" }}
                      checked={isResolved}
                      disabled={isSwitchDisabled}
                      onChange={handleSwitchChange}
                    />
                  </div>
                </div>
              </>
            )}
          </>
        </div>
      )
    );
  };

  const handleOnChangeReportActionChoice = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.target;

    if (value === "Select") {
      setCurrentMachineReportResponseId("");
      setValue("reportActionChoiceIds", []);
    } else {
      setCurrentMachineReportResponseId(value);
      setValue("reportActionChoiceIds", [value]);
    }

    if (value === initialDropdownValue || value === "Select") {
      setIsFormChanged(false);
    } else {
      setIsFormChanged(true);
    }
  };

  const handleSwitchChange = () => {
    const newStatus = !isResolved;
    setIsResolved(newStatus);
    setValue(
      "status",
      newStatus ? ResponseStatus.RESOLVED : ResponseStatus.UNADDRESSED,
    );

    setIsFormChanged(newStatus !== initialSwitchValue);
  };

  const handleOutputPdf = async () => {
    if (!!id && !!targetRow.machineId && !!targetRow.machineReportId) {
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const res = await apiGetReportMachineNameInfo(targetRow.machineReportId);
      const machineName = res?.data?.data?.customerMachineName;
      const timeFormat = res?.data?.data?.timeFormat;
      const reportName = t("abnormalityOrIssue");
      const reportTitle = res?.data?.data?.reportTitle;
      getMachinePDF(
        id,
        targetRow.machineId,
        targetRow.machineReportId,
        userTimeZone,
        pdfHeaderRemarks,
        pdfFooterRemarks,
        machineName,
        timeFormat,
        reportName,
        targetRow.reportedAt,
        reportTitle,
      )
        .then(() => {
          setPdfHeaderRemarks("");
          setPdfFooterRemarks("");
          setIncidentReportPDFdialogOpen(false);
          store.dispatch(setShowAlert(t("PDFDownloadedSuccessfully")));
        })
        .catch((error) => {
          console.error("PDF download failed:", error);
        });
    } else {
      console.error(
        "Missing required parameters: groupId, machineId, or machineReportId",
      );
    }
  };

  const scrollToBottom = () => {
    if (updateSectionRef.current) {
      updateSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  };

  const scrollToDialogBottom = () => {
    const smoothScrollToBottom = (element: Element) => {
      if (element.scrollHeight > element.clientHeight) {
        element.scrollTo({
          top: element.scrollHeight,
          behavior: "smooth",
        });
      }
    };

    if (contentRef.current) {
      smoothScrollToBottom(contentRef.current);
    }

    if (dialogRef.current) {
      smoothScrollToBottom(dialogRef.current);
    }

    const dialogPaper = document.querySelector(".MuiDialog-paper");
    if (dialogPaper) {
      smoothScrollToBottom(dialogPaper);
    }

    const dialogContent = document.querySelector(".MuiDialogContent-root");
    if (dialogContent) {
      smoothScrollToBottom(dialogContent);
    }

    const dialogContainer = document.querySelector(".MuiDialog-container");
    if (dialogContainer) {
      smoothScrollToBottom(dialogContainer);
    }

    const scrollableElements = document.querySelectorAll(
      '.content, .MuiDialogContent-root, .MuiDialog-paper, .MuiDialog-container, [class*="dialog"]',
    );
    scrollableElements.forEach((element) => {
      if (element.scrollHeight > element.clientHeight) {
        smoothScrollToBottom(element);
      }
    });
  };

  const handleInputPdfHeaderChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    const isInvalid =
      !REGEX.commonNameFieldCharacterMuiltiLineRegex.test(value) ||
      !REGEX.emojiRegex.test(value);

    setPdfHeaderRemarks(value);
    setPdfHeaderRemarksError(isInvalid);
  };

  const handleInputPdfFooterChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    const isInvalid =
      !REGEX.commonNameFieldCharacterMuiltiLineRegex.test(value) ||
      !REGEX.emojiRegex.test(value);

    setPdfFooterRemarks(value);
    setPdfFooterRemarksError(isInvalid);
  };

  const handleInputPdfMaintHeaderChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    const isInvalid =
      !REGEX.commonNameFieldCharacterMuiltiLineRegex.test(value) ||
      !REGEX.emojiRegex.test(value);

    setMaintPdfHeaderRemarks(value);
    setMaintPdfHeaderRemarksError(isInvalid);
  };

  const handleInputPdfMaintFooterChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    const isInvalid =
      !REGEX.commonNameFieldCharacterMuiltiLineRegex.test(value) ||
      !REGEX.emojiRegex.test(value);

    setMaintPdfFooterRemarks(value);
    setMaintPdfFooterRemarksError(isInvalid);
  };

  const handleOutputMaintenanceReportPdf = async (
    id: string,
    machineId: string,
    machineReportId: string,
  ) => {
    if (id && machineId && machineReportId) {
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await apiGetReportMachineNameInfoMaintenance(
        id,
        machineId,
        targetRow.machineReportId,
      );
      const machineName = res?.data?.data?.customerMachineName;
      const timeFormat = res?.data?.data?.timeFormat;
      const reportTitle = res?.data?.data?.reportTitle;
      const workAt = res?.data?.data?.workAt;
      getMachineMaintenancePDF(
        id,
        machineId,
        machineReportId,
        userTimeZone,
        pdfMaintHeaderRemarks,
        pdfMaintFooterRemarks,
        machineName,
        timeFormat,
        reportTitle,
        workAt,
      )
        .then(() => {
          setMaintPdfHeaderRemarks("");
          setMaintPdfFooterRemarks("");
          setMaintReportPDFdialogOpen(false);
          store.dispatch(setShowAlert(t("PDFDownloadedSuccessfully")));
        })
        .catch((error) => {
          console.error("PDF download failed:", error);
        });
    } else {
      console.error("Missing required parameters: groupId or machineId");
    }
  };

  const resetForm = (isCancelUpdate = false) => {
    if (!isCancelUpdate) {
      setOpenFormReport(INSPECTION);
      setTargetRow(null);
      dispatch(setIdReport(""));
    }
    setOpenUpdateStatus(false);
    setIsResolved(false);
    reset({
      reportComment: "",
    });
    setIsRead(false);
  };

  const handleClickConfirm = () => {
    if (isFormChanged) {
      setOpenDialogConfirm(true);
      setConfirmDialog({
        classBtnSubmit: "btn-primary",
        typeDialog: DialogTypes.CONFIRM,
        title: "titleConfirm",
        content: "",
        btnSubmitText: "ok",
        onCancel() {
          setOpenDialogConfirm(false);
        },
        onSubmit() {
          setOpenDialogConfirm(false);
          setValue("machineReportMedias", []);
          setIsFormChanged(false);
          resetForm();

          setOpenUpdateStatus(false);
          setIsUpdateSectionOpen(false);

          if (isRecording || isPaused) {
            stopRecording();
          }

          setRecordedBlob(null);
          setRecordingTime(0);
          setIsPaused(false);
          setIsRecording(false);
          setRecorder(null);
          setRecordingInterval(null);
          setMediaAvailable(true);

          setRecordingFiles([]);
          setRecordingCount(0);
          setExistingRecordingDeleted(false);

          dispatch(setIsUpdatedMachineReportResponses(`${new Date()}`));
          setOpen(false);
        },
      });
    } else {
      resetForm();
      setOpenUpdateStatus(false);
      setIsUpdateSectionOpen(false);

      if (isRecording || isPaused) {
        stopRecording();
      }

      setRecordedBlob(null);
      setRecordingTime(0);
      setIsPaused(false);
      setIsRecording(false);
      setRecorder(null);
      setRecordingInterval(null);
      setMediaAvailable(true);

      setRecordingFiles([]);
      setRecordingCount(0);
      setExistingRecordingDeleted(false);

      dispatch(setIsUpdatedMachineReportResponses(`${new Date()}`));
      setOpen(false);
    }
  };

  const startRecording = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasMic = devices.some((device) => device.kind === "audioinput");

      if (!hasMic) {
        setOpenFormNoMic(true);
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMediaStream(stream);

      if (recorder?.stream) {
        recorder.stream
          .getTracks()
          .forEach((track: MediaStreamTrack) => track.stop());
      }

      setIsPaused(false);
      setRecordedBlob(null);
      stoppedRef.current = false;

      const newRecorder = new RecordRTC(stream, {
        type: "audio",
        mimeType: "audio/webm",
        recorderType: RecordRTC.StereoAudioRecorder,
        numberOfAudioChannels: 1,
        desiredSampRate: 16000,
        audioBitsPerSecond: 64000,
      });

      recordingStartTimeRef.current = Date.now();
      newRecorder.startRecording();
      setRecorder(newRecorder);
      setIsRecording(true);
      setRecordingTime(0);

      const interval = setInterval(() => {
        if (recordingStartTimeRef.current) {
          const elapsed = Math.floor(
            (Date.now() - recordingStartTimeRef.current) / 1000,
          );
          setRecordingTime(Math.min(elapsed, 60));
        }
      }, 1000);

      setRecordingInterval(interval);

      const timeout = setTimeout(() => {
        stopRecording();
      }, 59200);

      recordingTimeoutRef.current = timeout;
    } catch (err) {
      console.warn("Microphone access denied", err);
      setMediaAvailable(false);
    }
  };

  const stopRecording = useCallback(() => {
    if (!recorder) return;

    stoppedRef.current = true;

    setIsRecording(false);
    setIsPaused(false);
    clearInterval(recordingInterval);
    if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);
    recorder.stopRecording(() => {
      const blob = recorder.getBlob();
      if (blob && blob.size > 0) {
        setRecordedBlob(blob);
      }

      if (mediaStream) {
        mediaStream
          .getTracks()
          .forEach((track: MediaStreamTrack) => track.stop());
        setMediaStream(null);
      }

      setRecorder(null);
      recordingStartTimeRef.current = null;
    });
  }, [recorder, recordingInterval, mediaStream]);

  const handleDeleteRecording = () => {
    if (isRecording || isPaused) {
      stopRecording();
    }

    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => {
        if (track.readyState === "live") {
          track.stop();
        }
      });
      setMediaStream(null);
    }

    if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);

    setRecordedBlob(null);
    setRecordingTime(0);
    setIsPaused(false);
    setIsRecording(false);
    setRecorder(null);
    setRecordingInterval(null);
    setMediaAvailable(true);

    setRecordingFiles([]);
    setRecordingCount(0);
    setExistingRecordingDeleted(true);
    setOpenFormDeleteRecording(false);
    recordingStartTimeRef.current = null;
  };

  const recordedBlobUrl = useMemo(() => {
    return recordedBlob ? URL.createObjectURL(recordedBlob) : null;
  }, [recordedBlob]);

  useEffect(() => {
    if (recordingTime >= 60 && isRecording && !stoppedRef.current) {
      stopRecording();
    }
  }, [recordingTime, isRecording, stopRecording]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getUser();
        let timeFormat = response?.data?.data?.timeFormat || "yyyy/MM/dd";
        let isoLocaleCode = response?.data?.data?.isoLocaleCode;
        const userId = response?.data?.data?.userId || "";

        if (!timeFormat.includes("HH") && !timeFormat.includes("mm")) {
          timeFormat += " HH:mm";
        }

        setUserTimeFormat(timeFormat);
        setLocaleCode(isoLocaleCode);
        setCurrentUserId(userId);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  const canEditOrDeleteMaintReport = useMemo(() => {
    if (openFormReport !== MAINTENANCE_REPORTS || !targetRow) {
      return false;
    }
    const reportUserId =
      targetRow.createdByUserId || targetRow.userId || targetRow.reporterUserId;
    if (!reportUserId || !currentUserId) return false;

    const isOwnReport = reportUserId === currentUserId;
    if (isOwnReport) {
      return (
        effectiveScreenPermission?.allowEditDeleteInspectionResult === true
      );
    }
    return (
      effectiveScreenPermission?.allowEditDeleteOthersMachineReports === true
    );
  }, [
    openFormReport,
    targetRow,
    currentUserId,
    effectiveScreenPermission?.allowEditDeleteInspectionResult,
    effectiveScreenPermission?.allowEditDeleteOthersMachineReports,
  ]);

  const handleOpenMaintOverflowMenu = () => {
    setMaintMenuAnchorEl(maintOverflowButtonRef.current);
  };

  const handleCloseMaintOverflowMenu = () => {
    setMaintMenuAnchorEl(null);
  };

  const canEditOrDeleteFuelReport = useMemo(() => {
    if (openFormReport !== FUEL_MAINTENANCE_REPORTS || !targetRow) {
      return false;
    }
    const reportUserId =
      targetRow.createdByUserId || targetRow.userId || targetRow.reporterUserId;
    if (!reportUserId || !currentUserId) return false;

    const isOwnReport = reportUserId === currentUserId;
    if (isOwnReport) {
      return (
        effectiveScreenPermission?.allowEditDeleteInspectionResult === true
      );
    }
    return (
      effectiveScreenPermission?.allowEditDeleteOthersMachineReports === true
    );
  }, [
    openFormReport,
    targetRow,
    currentUserId,
    effectiveScreenPermission?.allowEditDeleteInspectionResult,
    effectiveScreenPermission?.allowEditDeleteOthersMachineReports,
  ]);

  const handleOpenFuelOverflowMenu = () => {
    setFuelMenuAnchorEl(fuelOverflowButtonRef.current);
  };

  const handleCloseFuelOverflowMenu = () => {
    setFuelMenuAnchorEl(null);
  };

  const handleEditFuelReport = () => {
    handleCloseFuelOverflowMenu();
    if (!targetRow) return;

    setRefuelPrefill({
      mode: "update",
      machineReportId: targetRow.machineReportId || machineReportId,
      comment: targetRow.comment,
      serviceMeter: targetRow.serviceMeter ?? targetRow.serviceMeterInHour,
      odometer: targetRow.odometer,
      workAt: targetRow.workAt,
      isFuelRefilled: targetRow.isFuelRefilled,
      fuelAmount: targetRow.fuelAmount,
      isAdblueRefilled: targetRow.isAdblueRefilled,
      adblueAmount: targetRow.adblueAmount,
      machineReportMedias: targetRow.machineReportMedias,
      machineReportRecordings: targetRow.machineReportRecordings,
    });
    setOpenEditRefuel(true);
  };

  const handleCloseEditRefuel = () => {
    setOpenEditRefuel(false);
    setRefuelPrefill(null);
  };

  const confirmDeleteFuelReport = async () => {
    if (!id || !machineId || !machineReportId) return;

    try {
      setIsDeletingFuel(true);
      await apiDeleteMachineReport(id, machineId, machineReportId);
      store.dispatch(setShowAlert(t("alreadyDeleted")));
      dispatch(setIsUpdatedMachineReportResponses(`${new Date()}`));
      setOpenFuelDelete(false);
      handleCloseDialog();
      setOpen(false);
      onClose?.();
    } catch (error) {
      console.error("Failed to delete fuel report", error);
    } finally {
      setIsDeletingFuel(false);
    }
  };

  const confirmDeleteMaintReport = async () => {
    if (!id || !machineId || !machineReportId) return;

    try {
      setIsDeletingMaint(true);
      await apiDeleteMachineReport(id, machineId, machineReportId);
      store.dispatch(setShowAlert(t("alreadyDeleted")));
      dispatch(setIsUpdatedMachineReportResponses(`${new Date()}`));
      setOpenMaintDelete(false);
      handleCloseDialog();
      setOpen(false);
      onClose?.();
    } catch (error) {
      console.error("Failed to delete maintenance report", error);
    } finally {
      setIsDeletingMaint(false);
    }
  };

  const isAbnormalityFromInspection = useMemo(() => {
    if (!targetRow || openFormReport !== INCIDENT_REPORTS) return false;
    return targetRow.type === INSPECTION;
  }, [openFormReport, targetRow]);

  const canEditOrDeleteAbnormalityReport = useMemo(() => {
    if (openFormReport !== INCIDENT_REPORTS || !targetRow) {
      return false;
    }
    const reportUserId =
      targetRow.createdByUserId ||
      targetRow.userId ||
      targetRow.reporterUserId ||
      targetRow.machineReportResponses?.[0]?.userId;
    if (!reportUserId || !currentUserId) return false;

    const isOwnReport = reportUserId === currentUserId;
    if (isOwnReport) {
      return (
        effectiveScreenPermission?.allowEditDeleteInspectionResult === true
      );
    }
    return (
      effectiveScreenPermission?.allowEditDeleteOthersMachineReports === true
    );
  }, [
    openFormReport,
    targetRow,
    currentUserId,
    effectiveScreenPermission?.allowEditDeleteInspectionResult,
    effectiveScreenPermission?.allowEditDeleteOthersMachineReports,
  ]);

  const handleEditAbnormalityReport = async () => {
    handleCloseAbnormalityOverflowMenu();
    if (!targetRow || !id || !machineId || !machineReportId) return;

    if (isAbnormalityFromInspection) {
      setOpenAbnormalityFromInspectionHint(true);
      return;
    }

    try {
      setIsLoadingAbnormalityEdit(true);
      const res = await apiGetMachineReportOriginal(
        id,
        machineId,
        machineReportId,
      );
      const data = res?.data?.data;
      if (!data) return;

      const originalResponse = data.machineReportResponses?.[0];
      setAbnormalityPrefill({
        mode: "update",
        machineReportId: data.machineReportId || machineReportId,
        title: data.reportTitle,
        comment: originalResponse?.reportComment,
        serviceMeter:
          originalResponse?.serviceMeterInHour ??
          originalResponse?.serviceMeter,
        odometer: originalResponse?.odometer,
        machineReportMedias: originalResponse?.machineReportMedias,
        machineReportRecordings: originalResponse?.machineReportRecordings,
      });
      setOpenEditAbnormality(true);
    } catch (error) {
      console.error("Failed to load abnormality report for edit", error);
    } finally {
      setIsLoadingAbnormalityEdit(false);
    }
  };

  const handleCloseEditAbnormality = () => {
    setOpenEditAbnormality(false);
    setAbnormalityPrefill(null);
  };

  const handleDeleteAbnormalityReport = () => {
    handleCloseAbnormalityOverflowMenu();
    if (isAbnormalityFromInspection) {
      setOpenAbnormalityFromInspectionHint(true);
      return;
    }
    setOpenAbnormalityDelete(true);
  };

  const handleOpenAbnormalityOverflowMenu = () => {
    setAbnormalityMenuAnchorEl(abnormalityOverflowButtonRef.current);
  };

  const handleCloseAbnormalityOverflowMenu = () => {
    setAbnormalityMenuAnchorEl(null);
  };

  const handleAbnormalityPdfOutput = () => {
    handleCloseAbnormalityOverflowMenu();
    setPendingDialog("INCIDENT");
    setPdfConfirmOpen(true);
  };

  const confirmDeleteAbnormalityReport = async () => {
    if (!id || !machineId || !machineReportId) return;

    try {
      setIsDeletingAbnormality(true);
      await apiDeleteMachineReport(id, machineId, machineReportId);
      store.dispatch(setShowAlert(t("alreadyDeleted")));
      dispatch(setIsUpdatedMachineReportResponses(`${new Date()}`));
      setOpenAbnormalityDelete(false);
      handleCloseDialog();
      setOpen(false);
      onClose?.();
    } catch (error) {
      console.error("Failed to delete abnormality report", error);
    } finally {
      setIsDeletingAbnormality(false);
    }
  };

  useEffect(() => {
    const fetchMachineReportActionChoices = async () => {
      try {
        const res = await apiMachineReportActionChoices();
        setMachineReportActionChoices(res.data.data);
      } catch (error) {
        console.error("Error fetching Machine Report Action Choices:", error);
      }
    };

    fetchMachineReportActionChoices();
  }, []);

  useEffect(() => {
    if (currentMachineReportResponseId) {
      setValue("reportActionChoiceIds", [currentMachineReportResponseId]);
    }
  }, [currentMachineReportResponseId, setValue]);

  useEffect(() => {
    if (openFormReport === INCIDENT_REPORTS) {
      setInitialSwitchValue(isResolved);
      setIsFormChanged(false);

      setTimeout(() => {
        scrollToDialogBottom();
      }, 100);
    }
  }, [openFormReport, isResolved]);

  const [hasUserScrolled, setHasUserScrolled] = useState(false);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const handleScroll = () => {
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 50;
      if (!nearBottom) {
        setHasUserScrolled(true);
      } else {
        setHasUserScrolled(false);
      }
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  const [isSliding, setIsSliding] = useState(false);

  useEffect(() => {
    if (
      openFormReport === INCIDENT_REPORTS &&
      targetRow &&
      !hasUserScrolled &&
      !isSliding
    ) {
      setTimeout(() => {
        scrollToDialogBottom();
      }, 200);
    }
  }, [targetRow, openFormReport, hasUserScrolled, isSliding]);

  useEffect(() => {
    if (
      open &&
      openFormReport === INCIDENT_REPORTS &&
      !hasUserScrolled &&
      !isSliding
    ) {
      setTimeout(() => {
        scrollToDialogBottom();
      }, 200);
    }
  }, [open, openFormReport, targetRow, hasUserScrolled, isSliding]);

  useEffect(() => {
    return () => {
      if (recordedBlobUrl) {
        URL.revokeObjectURL(recordedBlobUrl);
      }
    };
  }, [recordedBlobUrl]);

  useEffect(() => {
    const hasAudio = !!recordedBlob || recordingFiles.length > 0;
    setIsFormChanged(hasAudio);
  }, [recordedBlob, recordingFiles]);

  useEffect(() => {
    if (id && machineId && machineReportId && isRead) {
      updateMachineReportStatus(id, machineId, machineReportId, isRead).then(
        () => {
          if (!!machineReportsOpen) {
            const newMachineReportsOpen = [...machineReportsOpen];
            const index = machineReportsOpen?.findIndex(
              (item) => item.machineReportId === machineReportId,
            );
            if (index > -1) {
              newMachineReportsOpen[index].isRead = true;
              setMachineReportsOpen(newMachineReportsOpen);
            }
          }
        },
      );
    }
  }, [id, machineId, machineReportId, isRead, machineReportsOpen]);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>("");

  return (
    <>
      <DataDialog open={open} setOpen={setOpen} onClose={onClose}></DataDialog>

      <DataDialog
        open={pdfConfirmOpen}
        disableBackdropClick={true}
        setOpen={() => setPdfConfirmOpen(false)}
        className="dialog-container"
      >
        <p className="fs-16" style={{ whiteSpace: "pre-line" }}>
          {t("pdfExpireWarning")}
        </p>

        <div
          className="flex justify-content-end group-btn-footer"
          style={{ gap: "12px" }}
        >
          <Button
            className="btn btn-no-border"
            onClick={() => {
              setPdfConfirmOpen(false);
              setPendingDialog(null);
            }}
          >
            {t("dialogReport.cancel")}
          </Button>

          <Button
            className="btn btn-primary ml-8"
            onClick={() => {
              setPdfConfirmOpen(false);

              if (pendingDialog === "MAINT") {
                setMaintReportPDFdialogOpen(true);
              }
              if (pendingDialog === "INCIDENT") {
                setIncidentReportPDFdialogOpen(true);
              }
            }}
          >
            {t("ok")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        open={MaintReportPDFdialogOpen}
        disableBackdropClick={true}
        setOpen={() => setMaintReportPDFdialogOpen(false)}
        title={
          <p
            style={{
              fontWeight: "bold",
              fontSize: "24px",
              marginBottom: "0px",
              marginTop: "0px",
              color: "black",
            }}
          >
            {t("PDFRemarks")}
          </p>
        }
        className="dialog-container"
      >
        <p className="txt-gray-1 fs-16">{t("pfdComments")}</p>
        <div className="wrapper-text-field mt-8">
          <TextField
            multiline
            rows={3}
            className="input-field"
            label={t("HeaderRemarks")}
            value={pdfMaintHeaderRemarks}
            fullWidth
            inputProps={{ maxLength: 1000 }}
            onChange={handleInputPdfMaintHeaderChange}
            error={pdfMaintHeaderRemarksError}
            helperText={
              pdfMaintHeaderRemarksError ? t("invalidCharacters") : ""
            }
            InputProps={{
              endAdornment: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  {pdfMaintHeaderRemarksError && <IconInputError />}
                </div>
              ),
            }}
          />
          {pdfMaintHeaderRemarksError && (
            <span className="error-text fs-12 ml-15 txt-red"></span>
          )}
        </div>

        <div className="wrapper-text-field mt-8">
          <TextField
            multiline
            rows={3}
            className="input-field"
            label={t("FooterRemarks")}
            value={pdfMaintFooterRemarks}
            fullWidth
            inputProps={{ maxLength: 1000 }}
            onChange={handleInputPdfMaintFooterChange}
            error={pdfMaintFooterRemarksError}
            helperText={
              pdfMaintFooterRemarksError ? t("invalidCharacters") : ""
            }
            InputProps={{
              endAdornment: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  {pdfMaintFooterRemarksError && <IconInputError />}
                </div>
              ),
            }}
          />
          {pdfMaintFooterRemarksError && (
            <span className="error-text fs-12 ml-15 txt-red"></span>
          )}
        </div>

        <div
          className="flex justify-content-end group-btn-footer"
          style={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "white",
            paddingTop: "16px",

            zIndex: 1000,
          }}
        >
          <Button
            className="btn btn-no-border"
            onClick={() => {
              setMaintReportPDFdialogOpen(false);

              setMaintPdfHeaderRemarks("");
              setMaintPdfFooterRemarks("");
            }}
          >
            {t("dialogReport.cancel")}
          </Button>

          <Button
            className={`btn btn-primary ml-8`}
            onClick={() => {
              handleOutputMaintenanceReportPdf(
                id ?? "",
                targetRow.machineId,
                targetRow.machineReportId,
              );
            }}
            disabled={pdfMaintHeaderRemarksError || pdfMaintFooterRemarksError}
          >
            {t("pdfOutput")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        open={IncidentReportPDFdialogOpen}
        disableBackdropClick={true}
        setOpen={() => setIncidentReportPDFdialogOpen(false)}
        title={
          <p
            style={{
              fontWeight: "bold",
              fontSize: "24px",
              marginBottom: "0px",
              marginTop: "0px",
              color: "black",
            }}
          >
            {t("PDFRemarks")}
          </p>
        }
        className="dialog-container"
      >
        <p className="txt-gray-1 fs-16">{t("pfdComments")}</p>
        <div className="wrapper-text-field mt-8">
          <TextField
            multiline
            rows={3}
            className="input-field"
            label={t("HeaderRemarks")}
            value={pdfHeaderRemarks}
            fullWidth
            inputProps={{ maxLength: 1000 }}
            onChange={handleInputPdfHeaderChange}
            error={pdfHeaderRemarksError}
            helperText={pdfHeaderRemarksError ? t("invalidCharacters") : ""}
            InputProps={{
              endAdornment: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  {pdfHeaderRemarksError && <IconInputError />}
                </div>
              ),
            }}
          />
          {pdfHeaderRemarksError && (
            <span className="error-text fs-12 ml-15 txt-red"></span>
          )}
        </div>

        <div className="wrapper-text-field mt-8">
          <TextField
            multiline
            rows={3}
            className="input-field"
            label={t("FooterRemarks")}
            value={pdfFooterRemarks}
            fullWidth
            inputProps={{ maxLength: 1000 }}
            onChange={handleInputPdfFooterChange}
            error={pdfFooterRemarksError}
            helperText={pdfFooterRemarksError ? t("invalidCharacters") : ""}
            InputProps={{
              endAdornment: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  {pdfFooterRemarksError && <IconInputError />}
                </div>
              ),
            }}
          />
          {pdfFooterRemarksError && (
            <span className="error-text fs-12 ml-15 txt-red"></span>
          )}
        </div>

        <div
          className="flex justify-content-end group-btn-footer"
          style={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "white",
            paddingTop: "16px",

            zIndex: 1000,
          }}
        >
          <Button
            className="btn btn-no-border"
            onClick={() => {
              setIncidentReportPDFdialogOpen(false);

              setPdfHeaderRemarks("");
              setPdfFooterRemarks("");
            }}
          >
            {t("dialogReport.cancel")}
          </Button>

          <Button
            className={`btn btn-primary ml-8`}
            onClick={() => {
              handleOutputPdf();
            }}
            disabled={pdfHeaderRemarksError || pdfFooterRemarksError}
          >
            {t("pdfOutput")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        ref={dialogRef}
        open={openFormReport === INCIDENT_REPORTS}
        onClose={handleCloseDialog}
        disableBackdropClick={true}
        title={
          isLoadingIncident ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Skeleton variant="text" width="45%" height={32} />
              <Skeleton
                variant="rectangular"
                width={110}
                height={36}
                sx={{ borderRadius: 1 }}
              />
            </Box>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                width: "100%",
                gap: "8px",
              }}
            >
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flex: "1 1 auto",
                  minWidth: 0,
                  fontSize: "21px",
                  fontWeight: "bold",
                  display: "block",
                }}
              >
                <DataTooltip isTextPre width="auto">
                  {targetRow?.reportTitle || ""}
                </DataTooltip>
              </span>
              <span
                ref={abnormalityOverflowButtonRef}
                style={{ flexShrink: 0, marginTop: "-4px" }}
              >
                <Button
                  className="bnt-action-form"
                  onClick={handleOpenAbnormalityOverflowMenu}
                >
                  <IconActionOverflow />
                </Button>
              </span>
            </div>
          )
        }
        subTitle={<>{subTitleDialog(targetRow)}</>}
        className={`dialog-report ${openUpdateStatus ? "full-height" : ""}  ${
          openFormReport === INSPECTION ? "d-none-imp" : "d-block"
        }`}
        maxWidth="md"
        fullWidth
        dialogActions={
          <ReportDialogSignatureFooter
            signatureUrl={targetRow?.signatureUrl}
            signatureBy={targetRow?.signatureBy}
            signatureAt={targetRow?.signatureAt}
            lastUpdatedSignatureUrl={targetRow?.lastUpdatedSignatureUrl}
            lastUpdatedSignatureBy={targetRow?.lastUpdatedSignatureBy}
            lastUpdatedSignatureAt={targetRow?.lastUpdatedSignatureAt}
            userTimeFormat={userTimeFormat}
          >
            <div
              className="flex justify-content-end group-btn-footer"
              style={{ gap: "12px" }}
            >
              <Button
                className="btn btn-no-border"
                onClick={handleClickConfirm}
                disabled={isRecording || isPaused}
              >
                {t("dialogReport.cancel")}
              </Button>

              <Button
                className={`btn btn-primary ml-8 ${
                  screenPermission?.allowCreateInspectionAndReport
                    ? "d-block"
                    : "d-none-imp"
                }`}
                onClick={handleSubmit(onSubmit)}
                disabled={!updateBtnDisableStatus()}
              >
                {t("formAction.save")}
              </Button>
            </div>
          </ReportDialogSignatureFooter>
        }
      >
        <Box component="form" autoComplete="off">
          <div
            ref={contentRef}
            className={`content ${
              openUpdateStatus ? "show-update" : "un-show-update"
            }`}
          >
            {isLoadingIncident ? (
              <IncidentReportSkeleton />
            ) : (
              openFormReport === INCIDENT_REPORTS &&
              targetRow?.machineReportResponses.map(
                (item: any, index: number) => {
                  const userPicture = getUserPicture(item, index);
                  const {
                    serviceMeterInHour,
                    odometer,
                    isChangeStatus,
                    isUpdated,
                    reportActionChoices,
                    reportResponseStatus: itemReportResponseStatus,
                    machineReportMedias: itemMachineReportMedias,
                    machineReportRecordings: itemMachineReportRecordings,
                    reportComment: itemReportComment,
                    subtype,
                    lat,
                    lng,
                  } = item;
                  const hasReportActionChoices = !!reportActionChoices.length;
                  const isReportResponseStatusResolved =
                    itemReportResponseStatus === ResponseStatus.RESOLVED;
                  const itemMachineReportMediasLength =
                    itemMachineReportMedias.length;
                  return (
                    <div className="report-card" key={item}>
                      <ReportLastUpdatedHeader
                        isUpdated={isUpdated}
                        lastUpdatedSignatureUrl={
                          targetRow?.lastUpdatedSignatureUrl
                        }
                        lastUpdatedSignatureBy={
                          targetRow?.lastUpdatedSignatureBy
                        }
                        lastUpdatedSignatureAt={
                          targetRow?.lastUpdatedSignatureAt
                        }
                        userTimeFormat={userTimeFormat}
                      />
                      <div className="content-report">
                        <div className="header flex align-items-center">
                          {userPicture}
                          <div className="report-detail">
                            <h3 className="report-name txt-black-1 word-break txt-pre-wrap">
                              {localeCode === "ja"
                                ? `${item.surname} ${item.givenName}`
                                : `${item.givenName} ${item.surname}`}
                            </h3>
                            <p className="report-time txt-black-1">
                              {item.timeSinceCommentCreation}
                            </p>
                          </div>
                        </div>

                        <div className="comment-section">
                          {(itemMachineReportMediasLength < 2 ||
                            itemReportComment) && (
                            <p
                              className={`text-content txt-black-1 txt-pre-wrap word-break ${
                                itemMachineReportMediasLength > 1 &&
                                itemReportComment &&
                                "pt-0"
                              }`}
                            >
                              {itemReportComment}
                            </p>
                          )}
                        </div>

                        {!!itemMachineReportRecordings?.length && (
                          <div
                            className="recordings-section"
                            style={{ marginTop: "12px", padding: "10px" }}
                          >
                            {itemMachineReportRecordings.map(
                              (recording: any, rIndex: number) => (
                                <div
                                  key={rIndex}
                                  style={{ marginBottom: "16px" }}
                                >
                                  <audio
                                    controls
                                    src={recording.recordingUrl}
                                    style={{ width: "100%" }}
                                  />
                                </div>
                              ),
                            )}
                          </div>
                        )}

                        {!!item.machineReportMedias?.length && (
                          <Swiper
                            onTouchStart={() => setIsSliding(true)}
                            onTouchEnd={() => setIsSliding(false)}
                            onSliderMove={() => setIsSliding(true)}
                            onTransitionEnd={() => setIsSliding(false)}
                            cssMode={true}
                            navigation={true}
                            pagination={true}
                            mousewheel={true}
                            keyboard={true}
                            modules={[
                              Navigation,
                              Pagination,
                              Mousewheel,
                              Keyboard,
                            ]}
                            className="swiper mt-20"
                          >
                            {itemMachineReportMedias
                              .filter(
                                (media: any) =>
                                  media?.mimeType !== "application/pdf",
                              )
                              .map((media: any, mediaIndex: number) => {
                                const isImage =
                                  media?.mimeType?.includes("image");
                                return (
                                  <SwiperSlide key={`${media}_${mediaIndex}`}>
                                    <div
                                      className="wrapper-file"
                                      onClick={() => {
                                        if (isImage) {
                                          setSelectedImage(media.mediaUrl);
                                          setViewerOpen(true);
                                        }
                                      }}
                                      style={{
                                        cursor: isImage ? "zoom-in" : "default",
                                      }}
                                    >
                                      {renderMedia(media, mediaIndex, index)}
                                    </div>

                                    <p
                                      className="created-at txt-gray-1 fs-12"
                                      style={{
                                        textAlign: "right",
                                        marginRight: "10px",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      {media.captureMediaAt ? (
                                        isNaN(
                                          new Date(
                                            media.captureMediaAt,
                                          ).getTime(),
                                        ) ? (
                                          <span style={{ color: "red" }}></span>
                                        ) : (
                                          `${format(
                                            new Date(
                                              new Date(
                                                media.captureMediaAt,
                                              ).toLocaleString("en-US", {
                                                timeZone:
                                                  Intl.DateTimeFormat().resolvedOptions()
                                                    .timeZone,
                                              }),
                                            ),
                                            userTimeFormat,
                                          )}`
                                        )
                                      ) : (
                                        ""
                                      )}
                                    </p>
                                  </SwiperSlide>
                                );
                              })}
                          </Swiper>
                        )}

                        {!!itemMachineReportMedias?.some(
                          (m: any) => m?.mimeType === "application/pdf",
                        ) && (
                          <div className="pdf-section mt-12">
                            {itemMachineReportMedias
                              .filter(
                                (media: any) =>
                                  media?.mimeType === "application/pdf",
                              )
                              .map((pdf: any, pdfIndex: number) => (
                                <div
                                  key={`pdf-${pdfIndex}`}
                                  onClick={() =>
                                    window.open(pdf.mediaUrl, "_blank")
                                  }
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    backgroundColor: "#f9f9f9",
                                    border: "1px solid #ddd",
                                    borderRadius: "8px",
                                    padding: "8px 12px",
                                    marginBottom: "10px",
                                    marginRight: "12px",
                                    marginLeft: "12px",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                      "#f0f0f0")
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                      "#f9f9f9")
                                  }
                                >
                                  <PdfFileIcon
                                    sx={{ fontSize: 28, color: "#b01c1c" }}
                                  />

                                  <span
                                    style={{
                                      fontSize: "14px",
                                      color: "#333",
                                      textDecoration: "underline",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {pdf.fileName || `PDF File ${pdfIndex + 1}`}
                                  </span>
                                </div>
                              ))}
                          </div>
                        )}

                        {lat && lng && (
                          <div className="text-content">
                            <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap">
                              {t("tableMachineDetail.location")}
                            </p>
                            <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
                              <span
                                style={{
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                                onClick={() => {
                                  setRecord({
                                    ...item,
                                    reportedAt: targetRow?.reportedAt,
                                  });
                                  setOpenLocation(true);
                                }}
                              >
                                <span
                                  style={{
                                    textDecoration: "underline",
                                    color: "#140a9a",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {t("openMap")}
                                </span>
                                <LaunchIcon
                                  sx={{ fontSize: 16, color: "black" }}
                                />
                              </span>
                            </p>
                          </div>
                        )}

                        {subtype !== STATUS_UPDATES && (
                          <>
                            <div className="text-content">
                              <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap">
                                {t("serviceMeterOrSMR")}
                              </p>
                              <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
                                {serviceMeterInHour !== null
                                  ? formatNumberWithCommas(
                                      serviceMeterInHour,
                                      "h",
                                    )
                                  : "-"}
                              </p>
                            </div>
                            {odometer !== null && (
                              <div className="text-content">
                                <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap">
                                  {t("odometer")}
                                </p>
                                <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
                                  {formatNumberWithCommas(odometer, "km")}
                                </p>
                              </div>
                            )}
                          </>
                        )}

                        {index === 0 && (
                          <ReportSignatureField
                            signatureUrl={targetRow?.signatureUrl}
                            signatureBy={targetRow?.signatureBy}
                            signatureAt={targetRow?.signatureAt}
                            userTimeFormat={userTimeFormat}
                          />
                        )}
                      </div>

                      {(isChangeStatus || hasReportActionChoices) && (
                        <div className="status">
                          <div className="content-status">
                            {hasReportActionChoices && (
                              <div className="txt-gray-1">
                                {t("dialogReport.repairAction")}:
                                {handleShowReportAction(reportActionChoices)}
                              </div>
                            )}
                            {isChangeStatus && (
                              <div
                                className={`${
                                  hasReportActionChoices ? "pt-44" : ""
                                } txt-gray-1`}
                              >
                                {t("dialogReport.status")}:
                                {isReportResponseStatusResolved ? (
                                  <span
                                    className={`status-resolved ml-5 active`}
                                  >
                                    {t("resolved")}
                                  </span>
                                ) : (
                                  <span className="status-resolved ml-5">
                                    {t("open")}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                },
              )
            )}
          </div>

          <div>
            <div
              ref={updateSectionRef}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "0px",
                marginBottom: "-20px",
                backgroundColor: "#f5f6f9",
                boxShadow: "0px -4px 6px rgba(0, 0, 0, 0.2)",
                height: "50px",
              }}
            >
              <Button
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  background: "transparent",
                  border: "none",
                  padding: "6px 12px",
                  fontSize: "14px",
                  color: "black",
                  fontWeight: "bold",
                  textTransform: "none",
                }}
                className="toggle-arrow-btn"
                onClick={() => {
                  setIsUpdateSectionOpen((prev) => {
                    const newState = !prev;
                    if (newState) {
                      setTimeout(scrollToBottom, 100);
                    }
                    return newState;
                  });
                }}
              >
                {isUpdateSectionOpen ? (
                  <KeyboardArrowUp
                    style={{ fontSize: "24px", color: "black" }}
                  />
                ) : (
                  <KeyboardArrowDown
                    style={{ fontSize: "24px", color: "black" }}
                  />
                )}
                <span style={{ marginRight: "6px" }}>
                  {t("addcommentfile")}
                </span>
              </Button>
            </div>

            {isUpdateSectionOpen && (
              <div className="update-status" ref={updateSectionRef}>
                <FormGroup>
                  <div className="wrapper-text-field mt-8">
                    <TextField
                      multiline
                      rows={3}
                      className="input-field"
                      label={t("dialogReport.comment")}
                      id={
                        errors?.reportComment?.type
                          ? "outlined-error"
                          : "outlined-controlled"
                      }
                      error={
                        !!errors?.reportComment?.type &&
                        errors?.reportComment?.type !== "maxLength"
                      }
                      {...register("reportComment", {
                        validate: {
                          hasEmoji: (value) => REGEX.emojiRegex.test(value),
                          hasInvalidChar: (value) =>
                            REGEX.commonNameFieldCharacterMuiltiLineRegex.test(
                              value,
                            ),
                        },
                        maxLength: 1000,
                        onBlur: (e) => {
                          const trimmedValue = e.target.value.trim();
                          if (errors?.reportComment?.type === "maxLength") {
                            setValue("reportComment", prevReportComment);
                          } else {
                            setValue("reportComment", e.target.value?.trim());
                          }
                          setIsFormChanged(trimmedValue !== "");
                          setIsReportCommentInputFocus(false);
                        },
                        onChange: (e) => {
                          const value = e.target.value;
                          if (!(errors?.reportComment?.type === "maxLength")) {
                            setPrevReportComment(e.target.value.slice(0, -1));
                            setIsFormChanged(true);
                          }
                          setIsFormChanged(value.trim() !== "");
                        },
                      })}
                      onFocus={() => setIsReportCommentInputFocus(true)}
                      onClick={() => {
                        if (
                          isReportCommentInputFocus &&
                          errors?.reportComment?.type === "maxLength"
                        ) {
                          setValue("reportComment", prevReportComment);
                        }
                      }}
                      inputProps={{
                        maxLength: 1000,
                        style: {
                          paddingRight: "65px",
                        },
                      }}
                    />

                    {mediaAvailable &&
                      !recordedBlob &&
                      recordingFiles.length === 0 && (
                        <div
                          style={{
                            position: "absolute",
                            right: 12,
                            top: 40,
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            zIndex: 10,
                            pointerEvents: "auto",
                          }}
                        >
                          {!isRecording && !isPaused ? (
                            <Tooltip title={t("start")}>
                              <FiberManualRecordIcon
                                onClick={
                                  recordingTime >= 60
                                    ? undefined
                                    : startRecording
                                }
                                sx={{
                                  color:
                                    recordingTime >= 60 ? "#ccc" : "#d32f2f",
                                  cursor:
                                    recordingTime >= 60
                                      ? "not-allowed"
                                      : "pointer",
                                  pointerEvents:
                                    recordingTime >= 60 ? "none" : "auto",
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    color:
                                      recordingTime >= 60 ? "#ccc" : "#a30000",
                                    transform:
                                      recordingTime >= 60
                                        ? "none"
                                        : "scale(1.1)",
                                  },
                                }}
                              />
                            </Tooltip>
                          ) : (
                            <>
                              <Tooltip title={t("stop")}>
                                <StopIcon
                                  onClick={stopRecording}
                                  sx={{
                                    color: "#d32f2f",
                                    cursor: "pointer",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                      color: "#a30000",
                                      transform: "scale(1.1)",
                                    },
                                  }}
                                />
                              </Tooltip>
                            </>
                          )}
                        </div>
                      )}
                  </div>

                  {errors?.reportComment?.type && (
                    <p className="txt-red fs-12 mt-0 mb-0 ml-15">
                      {errors?.reportComment?.type === "hasInvalidChar" &&
                        t("messages.validCharacters")}
                      {errors?.reportComment?.type === "hasEmoji" &&
                        t("messages.validCharacters")}
                    </p>
                  )}

                  {isRecording && recordingTime < 60 && (
                    <Tooltip title={t("recording")} arrow>
                      <div
                        className="glow-red"
                        style={{
                          marginTop: "4px",
                          fontSize: "12px",
                          color: "#d32f2f",
                          backgroundColor: "#ffe6e6",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          boxShadow: "0 0 8px #f44336",
                          fontWeight: "bold",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          height: "32px",
                          width: "60px",
                        }}
                      >
                        <FiberManualRecordIcon fontSize="small" />

                        <span
                          style={{ transform: "translateY(1px)" }}
                        >{`${recordingTime}s`}</span>
                      </div>
                    </Tooltip>
                  )}

                  {isPaused && (
                    <Tooltip title={t("paused")} arrow>
                      <div
                        className="glow-yellow"
                        style={{
                          marginTop: "4px",
                          fontSize: "12px",
                          color: "#f9a825",
                          backgroundColor: "#fff8e1",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          boxShadow: "0 0 8px #fbc02d",
                          fontWeight: "bold",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          height: "32px",
                          width: "60px",
                        }}
                      >
                        <PauseCircleIcon fontSize="small" />
                        {`${recordingTime}s`}
                      </div>
                    </Tooltip>
                  )}

                  {recordedBlob ? (
                    <div
                      style={{
                        marginTop: "12px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <audio
                          controls
                          src={recordedBlobUrl!}
                          style={{ flex: 1 }}
                        />
                        <Tooltip title={t("formAction.delete")}>
                          <IconButton
                            size="small"
                            onClick={() => setOpenFormDeleteRecording(true)}
                            sx={{
                              color: "#d32f2f",
                              marginLeft: 2,
                              borderRadius: "50%",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                border: "2px solid #d32f2f",
                                backgroundColor: "transparent",
                              },
                            }}
                          >
                            <IconDeleteRed />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                  ) : !existingRecordingDeleted && recordingFiles.length > 0 ? (
                    <div
                      style={{
                        marginTop: "12px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <audio
                          controls
                          src={recordingFiles[0].recordingUrl}
                          style={{ flex: 1 }}
                        />

                        <Tooltip title={t("formAction.delete")}>
                          <IconButton
                            size="small"
                            onClick={() => setOpenFormDeleteRecording(true)}
                            sx={{
                              color: "#d32f2f",
                              marginLeft: 2,
                              borderRadius: "50%",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                border: "2px solid #d32f2f",
                                backgroundColor: "transparent",
                              },
                            }}
                          >
                            <IconDeleteRed />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                  ) : null}

                  {(isRecording || isPaused) && (
                    <div
                      className="warning flex justify-content-start"
                      style={{ marginBottom: 12 }}
                    >
                      <IconWarningFormEdit />
                      <span
                        style={{ marginLeft: 12, flex: 1 }}
                        className="txt-black-1"
                      >
                        {t("warningUpdateItem3")}
                      </span>
                    </div>
                  )}

                  <div className="update-media-upload-file mt-24 mb-24 w-100">
                    <MediaUploadMachineReportHistory
                      medias={currentMedias}
                      setMedias={(files: File[]) => {
                        setValue("machineReportMedias", files);

                        setIsFormChanged(files.length > 0);
                      }}
                      uploadDescription={uploadDescription}
                      errMessage={mediaUploadErrorMsg}
                      typeFile={TYPE_FILE_UPLOAD.pdfImageVideo}
                      maxFiles={5}
                      showMaxUploadLimit={true}
                    />
                  </div>
                </FormGroup>
              </div>
            )}
          </div>
        </Box>
      </DataDialog>

      <DataDialog
        open={openFormReport === FUEL_MAINTENANCE_REPORTS}
        onClose={handleCloseDialog}
        disableBackdropClick={true}
        title={
          isLoadingFuelMaintenance ? (
            <Skeleton variant="text" width="55%" height={32} />
          ) : (
            <span
              title={targetRow?.reportTitle}
              style={{
                display: "inline-flex",
                alignItems: "center",
                fontSize: 22,
                fontFamily: "inherit",
                minWidth: 0,
              }}
              dangerouslySetInnerHTML={{
                __html: (targetRow?.reportTitle || "").replace(
                  /®/g,
                  '<sup style="font-size:0.65em;vertical-align:super;line-height:0;margin-left:1px;">®</sup>',
                ),
              }}
            />
          )
        }
        action={
          !isLoadingFuelMaintenance && canEditOrDeleteFuelReport ? (
            <span ref={fuelOverflowButtonRef}>
              <Button
                className="bnt-action-form"
                onClick={handleOpenFuelOverflowMenu}
              >
                <IconActionOverflow />
              </Button>
            </span>
          ) : undefined
        }
        subTitle={
          isLoadingFuelMaintenance ? (
            <SubTitleSkeleton showControls={false} />
          ) : (
            !!targetRow && (
              <div className="sub-title-dialog" style={{ width: "100%" }}>
                <>
                  {targetRow?.reportedAt &&
                  !isNaN(new Date(targetRow.reportedAt).getTime())
                    ? format(new Date(targetRow.reportedAt), userTimeFormat)
                    : ""}
                </>
              </div>
            )
          )
        }
        className="dialog-report"
        dialogActions={
          <ReportDialogSignatureFooter
            signatureUrl={targetRow?.signatureUrl}
            signatureBy={targetRow?.signatureBy}
            signatureAt={targetRow?.signatureAt}
            lastUpdatedSignatureUrl={targetRow?.lastUpdatedSignatureUrl}
            lastUpdatedSignatureBy={targetRow?.lastUpdatedSignatureBy}
            lastUpdatedSignatureAt={targetRow?.lastUpdatedSignatureAt}
            userTimeFormat={userTimeFormat}
          >
            <Button
              onClick={() => {
                handleCloseDialog();
                setOpen(false);
              }}
              className="btn btn-no-border"
            >
              {t("dialogRefuel.close")}
            </Button>
          </ReportDialogSignatureFooter>
        }
      >
        {isLoadingFuelMaintenance ? (
          <div className="content">
            <FuelMaintenanceReportSkeleton />
          </div>
        ) : (
          openFormReport === FUEL_MAINTENANCE_REPORTS &&
          targetRow && (
            <div className="content">
              <ReportLastUpdatedHeader
                isUpdated={targetRow.isUpdated}
                lastUpdatedSignatureUrl={targetRow?.lastUpdatedSignatureUrl}
                lastUpdatedSignatureBy={targetRow?.lastUpdatedSignatureBy}
                lastUpdatedSignatureAt={targetRow?.lastUpdatedSignatureAt}
                userTimeFormat={userTimeFormat}
              />
              <div className="report-card">
                <div className="content-report">
                  <div className="header flex align-items-center">
                    {getUserPicture(targetRow)}
                    <div className="report-detail">
                      <h3 className="report-name txt-black-1 word-break txt-pre-wrap">
                        {targetRow.reporterName}
                      </h3>
                      <p className="report-time txt-black-1">
                        {targetRow.timeSinceCommentCreation}
                      </p>
                    </div>
                  </div>
                  <div className="text-content">
                    <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap">
                      {t("dialogMaintenanceReports.workTime")}
                    </p>
                    <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
                      {targetRow.workAt &&
                      !isNaN(new Date(targetRow.workAt).getTime())
                        ? format(new Date(targetRow.workAt), userTimeFormat)
                        : "-"}
                    </p>
                  </div>
                  {targetRow.lat && targetRow.lng && (
                    <div className="text-content">
                      <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap">
                        {t("tableMachineDetail.location")}
                      </p>
                      <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
                        <span
                          style={{
                            cursor: "pointer",
                            fontSize: "14px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                          onClick={() => {
                            setRecord(targetRow);
                            setOpenLocation(true);
                          }}
                        >
                          <span
                            style={{
                              textDecoration: "underline",
                              color: "#140a9a",
                              fontWeight: "bold",
                            }}
                          >
                            {t("openMap")}
                          </span>
                          <LaunchIcon sx={{ fontSize: 16, color: "black" }} />
                        </span>
                      </p>
                    </div>
                  )}
                  <div className="text-content">
                    <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap">
                      {t("serviceMeterOrSMR")}
                    </p>

                    <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
                      <span dir="ltr">
                        {formatNumberWithCommas(targetRow.serviceMeter, "h")}
                      </span>
                    </p>
                  </div>
                  {targetRow.odometer !== null && (
                    <div className="text-content">
                      <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap">
                        {t("odometer")}
                      </p>

                      <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
                        <span dir="ltr">
                          {formatNumberWithCommas(targetRow.odometer, "km")}
                        </span>
                      </p>
                    </div>
                  )}
                  <ReportSignatureField
                    signatureUrl={targetRow?.signatureUrl}
                    signatureBy={targetRow?.signatureBy}
                    signatureAt={targetRow?.signatureAt}
                    userTimeFormat={userTimeFormat}
                  />
                </div>
              </div>
              <div className="report-card">
                <div className="content-report">
                  {targetRow.isFuelRefilled && (
                    <div className="text-content">
                      <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap">
                        {t("dialogRefuel.oilWaterAmount")}
                      </p>

                      <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
                        <span dir="ltr">
                          {targetRow.fuelAmount != null
                            ? formatNumberWithCommas(targetRow.fuelAmount, "L")
                            : "-"}
                        </span>
                      </p>
                    </div>
                  )}

                  {targetRow.isAdblueRefilled && (
                    <div className="text-content">
                      <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap">
                        {t("dialogRefuel.AdBlue")}
                      </p>

                      <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
                        <span dir="ltr">
                          {targetRow.adblueAmount != null
                            ? formatNumberWithCommas(
                                targetRow.adblueAmount,
                                "L",
                              )
                            : "-"}
                        </span>
                      </p>
                    </div>
                  )}

                  {targetRow.comment && (
                    <div className="text-content" style={{ marginTop: -12 }}>
                      <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap fw-500">
                        {t("dialogReport.comment")}
                      </p>
                      <p
                        className={`txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap word-break ${
                          targetRow.machineReportMedias.length > 1 &&
                          targetRow.comment &&
                          "pt-0"
                        } `}
                      >
                        {targetRow.comment}
                      </p>
                    </div>
                  )}

                  {!!targetRow.machineReportRecordings?.length && (
                    <div
                      className="recordings-section"
                      style={{ marginTop: "12px", padding: "10px" }}
                    >
                      {targetRow.machineReportRecordings.map(
                        (recording: any, rIndex: number) => (
                          <div key={rIndex} style={{ marginBottom: "16px" }}>
                            <audio
                              controls
                              src={recording.recordingUrl}
                              style={{ width: "100%" }}
                            />
                          </div>
                        ),
                      )}
                    </div>
                  )}

                  <Swiper
                    cssMode={true}
                    navigation={true}
                    pagination={true}
                    mousewheel={true}
                    keyboard={true}
                    modules={[Navigation, Pagination, Mousewheel, Keyboard]}
                    className="swiper"
                  >
                    {targetRow.machineReportMedias.map(
                      (media: any, mediaIndex: number) => {
                        const isImage = media?.mimeType?.includes("image");
                        return (
                          <SwiperSlide key={`${media}_${mediaIndex}`}>
                            <div className="wrapper-file">
                              <div
                                className="wrapper-file"
                                onClick={() => {
                                  if (isImage) {
                                    setSelectedImage(media.mediaUrl);
                                    setViewerOpen(true);
                                  }
                                }}
                                style={{
                                  cursor: isImage ? "zoom-in" : "default",
                                }}
                              >
                                {renderMedia(media, mediaIndex)}
                              </div>
                            </div>

                            <p
                              className="created-at txt-gray-1 fs-12"
                              style={{
                                textAlign: "right",
                                marginRight: "10px",
                                fontWeight: "bold",
                              }}
                            >
                              {media.captureMediaAt ? (
                                isNaN(
                                  new Date(media.captureMediaAt).getTime(),
                                ) ? (
                                  <span style={{ color: "red" }}></span>
                                ) : (
                                  `${format(
                                    new Date(
                                      new Date(
                                        media.captureMediaAt,
                                      ).toLocaleString("en-US", {
                                        timeZone:
                                          Intl.DateTimeFormat().resolvedOptions()
                                            .timeZone,
                                      }),
                                    ),
                                    userTimeFormat,
                                  )}`
                                )
                              ) : (
                                ""
                              )}
                            </p>
                          </SwiperSlide>
                        );
                      },
                    )}
                  </Swiper>
                </div>
              </div>
            </div>
          )
        )}
      </DataDialog>

      <Menu
        anchorEl={maintMenuAnchorEl}
        open={Boolean(maintMenuAnchorEl)}
        onClose={handleCloseMaintOverflowMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setPendingDialog("MAINT");
            setPdfConfirmOpen(true);
          }}
        >
          <ListItemIcon>
            <PictureAsPdfOutlined
              style={{ fontSize: "20px", color: "#140a9a" }}
            />
          </ListItemIcon>
          <ListItemText primary={t("pdfOutput")} />
        </MenuItem>
        {canEditOrDeleteMaintReport && (
          <MenuItem
            onClick={() => {
              handleCloseMaintOverflowMenu();
              setOpenMaintDelete(true);
            }}
          >
            <ListItemIcon>
              <IconDelete />
            </ListItemIcon>
            <ListItemText
              primary={t("formAction.delete")}
              primaryTypographyProps={{ style: { color: "#DA093B" } }}
            />
          </MenuItem>
        )}
      </Menu>

      <Menu
        anchorEl={fuelMenuAnchorEl}
        open={Boolean(fuelMenuAnchorEl)}
        onClose={handleCloseFuelOverflowMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleEditFuelReport}>
          <ListItemIcon>
            <IconEdit />
          </ListItemIcon>
          <ListItemText primary={t("formAction.edit")} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseFuelOverflowMenu();
            setOpenFuelDelete(true);
          }}
        >
          <ListItemIcon>
            <IconDelete />
          </ListItemIcon>
          <ListItemText
            primary={t("formAction.delete")}
            primaryTypographyProps={{ style: { color: "#DA093B" } }}
          />
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={abnormalityMenuAnchorEl}
        open={Boolean(abnormalityMenuAnchorEl)}
        onClose={handleCloseAbnormalityOverflowMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {canEditOrDeleteAbnormalityReport && (
          <MenuItem
            onClick={handleEditAbnormalityReport}
            disabled={isLoadingAbnormalityEdit}
          >
            <ListItemIcon>
              <IconEdit />
            </ListItemIcon>
            <ListItemText primary={t("formAction.edit")} />
          </MenuItem>
        )}
        <MenuItem onClick={handleAbnormalityPdfOutput}>
          <ListItemIcon>
            <PictureAsPdfOutlined
              style={{ fontSize: "20px", color: "#140a9a" }}
            />
          </ListItemIcon>
          <ListItemText primary={t("pdfOutput")} />
        </MenuItem>
        {canEditOrDeleteAbnormalityReport && (
          <MenuItem
            onClick={handleDeleteAbnormalityReport}
            disabled={isLoadingAbnormalityEdit || isDeletingAbnormality}
          >
            <ListItemIcon>
              <IconDelete />
            </ListItemIcon>
            <ListItemText
              primary={t("formAction.delete")}
              primaryTypographyProps={{ style: { color: "#DA093B" } }}
            />
          </MenuItem>
        )}
      </Menu>

      <DataDialog
        title={t("titleDeleteCard")}
        open={openMaintDelete}
        setOpen={setOpenMaintDelete}
        disableBackdropClick={true}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("desDeleteCard")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => setOpenMaintDelete(false)}
              className="btn btn-no-border"
              disabled={isDeletingMaint}
            >
              {t("dialogReport.cancel")}
            </Button>
            <Button
              onClick={confirmDeleteMaintReport}
              className="btn-delete"
              disabled={isDeletingMaint}
            >
              {t("formAction.delete")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        title={t("titleDeleteCard")}
        open={openFuelDelete}
        setOpen={setOpenFuelDelete}
        disableBackdropClick={true}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("desDeleteCard")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => setOpenFuelDelete(false)}
              className="btn btn-no-border"
              disabled={isDeletingFuel}
            >
              {t("dialogReport.cancel")}
            </Button>
            <Button
              onClick={confirmDeleteFuelReport}
              className="btn-delete"
              disabled={isDeletingFuel}
            >
              {t("formAction.delete")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <CreateRefuel
        open={openEditRefuel}
        onClose={handleCloseEditRefuel}
        groupId={id || ""}
        machineId={machineId || ""}
        prefill={refuelPrefill}
        onSuccess={() => {
          dispatch(setIsUpdatedMachineReportResponses(`${new Date()}`));
          if (id && machineId && machineReportId) {
            getFuelMaintenanceReport(id, machineId, machineReportId);
          }
          handleCloseEditRefuel();
        }}
      />

      <DataDialog
        title={t("titleDeleteCard")}
        open={openAbnormalityDelete}
        setOpen={setOpenAbnormalityDelete}
        disableBackdropClick={true}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("desDeleteCard")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => setOpenAbnormalityDelete(false)}
              className="btn btn-no-border"
              disabled={isDeletingAbnormality}
            >
              {t("dialogReport.cancel")}
            </Button>
            <Button
              onClick={confirmDeleteAbnormalityReport}
              className="btn-delete"
              disabled={isDeletingAbnormality}
            >
              {t("formAction.delete")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        open={openAbnormalityFromInspectionHint}
        setOpen={setOpenAbnormalityFromInspectionHint}
        disableBackdropClick={true}
        className="dialog-container"
      >
        <p className="fs-16" style={{ whiteSpace: "pre-line" }}>
          {t("createAbnormality.fromInspectionEditHint")}
        </p>
        <div
          className="flex justify-content-end group-btn-footer"
          style={{ gap: "12px" }}
        >
          <Button
            className="btn btn-primary"
            onClick={() => setOpenAbnormalityFromInspectionHint(false)}
          >
            {t("ok")}
          </Button>
        </div>
      </DataDialog>

      <CreateAbnormality
        open={openEditAbnormality}
        onClose={handleCloseEditAbnormality}
        groupId={id || ""}
        machineId={machineId || ""}
        prefill={abnormalityPrefill}
        onSuccess={() => {
          dispatch(setIsUpdatedMachineReportResponses(`${new Date()}`));
          if (id && machineId && machineReportId) {
            getMachineReportResponses(id, machineId, machineReportId);
          }
          handleCloseEditAbnormality();
        }}
      />

      <DataDialog
        open={
          reportType === MAINTENANCE_REPORTS &&
          (reportVersion === "V1" || reportVersion === "V2")
        }
        onClose={handleCloseDialog}
        disableBackdropClick={true}
        title={
          isLoadingMaintenance ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Skeleton variant="text" width="50%" height={30} />
              <Skeleton
                variant="rectangular"
                width={110}
                height={36}
                sx={{ borderRadius: 1 }}
              />
            </Box>
          ) : openFormReport === MAINTENANCE_REPORTS ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                minWidth: 0,
              }}
            >
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                  flex: "1 1 auto",
                  minWidth: 0,
                  fontWeight: "bold",
                  fontSize: "20px",
                }}
              >
                {(() => {
                  let title = "";

                  if (
                    targetRow?.regularMaintenanceItemChoice?.regularMaintenanceItemChoiceName?.trim()
                  ) {
                    title =
                      targetRow.regularMaintenanceItemChoice
                        .regularMaintenanceItemChoiceName;
                  } else {
                    const intervals: any[] = (
                      targetRow?.intervalMaintenanceItemChoices || []
                    ).map((interval: any) => ({
                      ...interval,
                      intervalMaintenanceItemChoiceName:
                        interval.intervalMaintenanceItemChoiceName ??
                        t("announcementTypeValues.OTHER"),
                    }));
                    const items = targetRow?.items || [];

                    let maxInterval: any = null;
                    let otherIntervals: string[] = [];

                    if (intervals.length > 0) {
                      maxInterval = intervals.reduce((max, curr) => {
                        const currVal = parseInt(
                          curr.intervalMaintenanceItemChoiceName.replace(
                            /[^0-9]/g,
                            "",
                          ),
                          10,
                        );
                        const maxVal = parseInt(
                          max.intervalMaintenanceItemChoiceName.replace(
                            /[^0-9]/g,
                            "",
                          ),
                          10,
                        );
                        return currVal > maxVal ? curr : max;
                      });

                      otherIntervals = intervals
                        .filter((i) => i !== maxInterval)
                        .map((i) => i.intervalMaintenanceItemChoiceName);
                    }

                    const maxIntervalName = maxInterval
                      ? `${maxInterval.intervalMaintenanceItemChoiceName} ${t(
                          "maintenanceReport",
                        )}`
                      : "";

                    const itemNames = items.map((item: any) =>
                      item.maintenanceItemOther?.trim()
                        ? item.maintenanceItemOther
                        : item.maintenanceItemName,
                    );

                    if (maxIntervalName) {
                      if (otherIntervals.length > 0) {
                        title += `${maxIntervalName} (${otherIntervals.join(
                          ", ",
                        )})`;
                      } else {
                        title += maxIntervalName;
                      }
                    }

                    if (itemNames.length > 0) {
                      title += (title ? ", " : "") + itemNames.join(", ");
                    }
                  }

                  const isTitleTruncated = title.length > 30;
                  const displayTitle = isTitleTruncated
                    ? `${title.slice(0, 30)}...`
                    : title;

                  return (
                    <DataTooltip
                      isTextPre
                      width="auto"
                      alwaysShow={isTitleTruncated}
                      tooltipContent={title || t("")}
                    >
                      {displayTitle || t("")}
                    </DataTooltip>
                  );
                })()}
              </span>
              {/* <Button
                className="`ml-8 btn btn-primary"
                style={{
                  gap: "8px",
                  whiteSpace: "nowrap",
                }}
                onClick={() => {
                  setPendingDialog("MAINT");
                  setPdfConfirmOpen(true);
                }}
              >
                <PictureAsPdfOutlined style={{ fontSize: "16px" }} />
                {t("pdfOutput")}
              </Button> */}
            </div>
          ) : (
            ""
          )
        }
        action={
          !isLoadingMaintenance ? (
            <span ref={maintOverflowButtonRef}>
              <Button
                className="bnt-action-form"
                onClick={handleOpenMaintOverflowMenu}
              >
                <IconActionOverflow />
              </Button>
            </span>
          ) : undefined
        }
        subTitle={
          isLoadingMaintenance ? (
            <SubTitleSkeleton showControls={false} />
          ) : (
            <>{subTitleDialog(targetRow)}</>
          )
        }
        className={`dialog-report ${
          openFormReport === INSPECTION ? "d-none-imp" : "d-block"
        }`}
        dialogActions={
          <ReportDialogSignatureFooter
            signatureUrl={targetRow?.signatureUrl}
            signatureBy={targetRow?.signatureBy}
            signatureAt={targetRow?.signatureAt}
            lastUpdatedSignatureUrl={targetRow?.lastUpdatedSignatureUrl}
            lastUpdatedSignatureBy={targetRow?.lastUpdatedSignatureBy}
            lastUpdatedSignatureAt={targetRow?.lastUpdatedSignatureAt}
            userTimeFormat={userTimeFormat}
          >
            <Button
              onClick={() => {
                handleCloseDialog();
                setOpen(false);
              }}
              className="btn btn-no-border"
            >
              {t("dialogRefuel.close")}
            </Button>
          </ReportDialogSignatureFooter>
        }
      >
        {isLoadingMaintenance ? (
          <div className="content">
            <MaintenanceReportSkeleton />
          </div>
        ) : (
          openFormReport === MAINTENANCE_REPORTS &&
          targetRow && (
            <div className="content">
              <div className="report-card">
                <ReportLastUpdatedHeader
                  isUpdated={targetRow.isUpdated}
                  lastUpdatedSignatureUrl={targetRow?.lastUpdatedSignatureUrl}
                  lastUpdatedSignatureBy={targetRow?.lastUpdatedSignatureBy}
                  lastUpdatedSignatureAt={targetRow?.lastUpdatedSignatureAt}
                  userTimeFormat={userTimeFormat}
                />
                <div className="content-report">
                  <div className="header flex align-items-center">
                    {getUserPicture(targetRow)}
                    <div className="report-detail">
                      <h3 className="report-name txt-black-1 word-break txt-pre-wrap">
                        {localeCode === "ja"
                          ? `${targetRow.surname} ${targetRow.givenName}`
                          : `${targetRow.givenName} ${targetRow.surname}`}
                      </h3>
                      <p className="report-time txt-black-1">
                        {targetRow.timeSinceCommentCreation}
                      </p>
                    </div>
                  </div>
                  <div
                    className={`text-content ${
                      targetRow.reportVersion === "V3"
                        ? "grid grid-cols pt-0"
                        : ""
                    }`}
                  >
                    <p
                      className={`${
                        targetRow.reportVersion === "V3"
                          ? "txt-black-1 fs-14"
                          : "txt-gray-1 fs-12"
                      } mt-0 mb-0 txt-pre-wrap fw-500`}
                    >
                      {t("dialogMaintenanceReports.workTime")}
                    </p>

                    <p
                      className={`txt-black-1 ${
                        targetRow.reportVersion === "V3" ? "fs-14" : "fs-16"
                      } mt-0 mb-0 txt-pre-wrap`}
                    >
                      {targetRow.workAt &&
                      !isNaN(new Date(targetRow.workAt).getTime())
                        ? format(new Date(targetRow.workAt), userTimeFormat)
                        : "-"}
                    </p>
                  </div>

                  {targetRow.lat && targetRow.lng ? (
                    <div
                      className={`text-content pt-0 ${
                        targetRow.reportVersion === "V3" ? "grid grid-cols" : ""
                      }`}
                    >
                      <p
                        className={`${
                          targetRow.reportVersion === "V3"
                            ? "txt-black-1 fs-14"
                            : "txt-gray-1 fs-12"
                        } mt-0 mb-0 txt-pre-wrap`}
                      >
                        {t("tableMachineDetail.location")}
                      </p>

                      <p
                        className={`txt-black-1 ${
                          targetRow.reportVersion === "V3" ? "fs-14" : "fs-16"
                        } mt-0 mb-0 txt-pre-wrap`}
                      >
                        <span
                          style={{
                            cursor: "pointer",
                            fontSize: "14px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                          onClick={() => {
                            setRecord(targetRow);
                            setOpenLocation(true);
                          }}
                        >
                          <span
                            style={{
                              textDecoration: "underline",
                              color: "#140a9a",
                              fontWeight: "bold",
                            }}
                          >
                            {t("openMap")}
                          </span>

                          <LaunchIcon sx={{ fontSize: 16, color: "black" }} />
                        </span>
                      </p>
                    </div>
                  ) : null}

                  {targetRow.serviceMeterInHour !== null && (
                    <div
                      className={`text-content pt-0 ${
                        targetRow.reportVersion === "V3" ? "grid grid-cols" : ""
                      }`}
                    >
                      <p
                        className={`${
                          targetRow.reportVersion === "V3"
                            ? "txt-black-1 fs-14"
                            : "txt-gray-1 fs-12"
                        } mt-0 mb-0 txt-pre-wrap fw-500`}
                      >
                        {t("serviceMeterOrSMR")}
                      </p>

                      <p
                        className={`txt-black-1 ${
                          targetRow.reportVersion === "V3" ? "fs-14" : "fs-16"
                        } mt-0 mb-0 txt-pre-wrap`}
                      >
                        <span dir="ltr">
                          {formatNumberWithCommas(
                            targetRow.serviceMeterInHour,
                            "h",
                          )}
                        </span>
                      </p>
                    </div>
                  )}

                  {targetRow.odometer !== null && (
                    <div
                      className={`text-content pt-0 ${
                        targetRow.reportVersion === "V3" ? "grid grid-cols" : ""
                      }`}
                    >
                      <p
                        className={`${
                          targetRow.reportVersion === "V3"
                            ? "txt-black-1 fs-14"
                            : "txt-gray-1 fs-12"
                        } mt-0 mb-0 txt-pre-wrap fw-500`}
                      >
                        {t("odometer")}
                      </p>

                      <p
                        className={`txt-black-1 ${
                          targetRow.reportVersion === "V3" ? "fs-14" : "fs-16"
                        } mt-0 mb-0 txt-pre-wrap`}
                      >
                        <span dir="ltr">
                          {formatNumberWithCommas(targetRow.odometer, "km")}
                        </span>
                      </p>
                    </div>
                  )}

                  <ReportSignatureField
                    signatureUrl={targetRow?.signatureUrl}
                    signatureBy={targetRow?.signatureBy}
                    signatureAt={targetRow?.signatureAt}
                    userTimeFormat={userTimeFormat}
                    className={`text-content pt-0 ${
                      targetRow.reportVersion === "V3" ? "grid grid-cols" : ""
                    }`}
                    labelClassName={`${
                      targetRow.reportVersion === "V3"
                        ? "txt-black-1 fs-14"
                        : "txt-gray-1 fs-12"
                    } mt-0 mb-0 txt-pre-wrap fw-500`}
                  />
                </div>
              </div>

              {targetRow.reportVersion === "V1" && (
                <div className="report-card">
                  <div className="content-report">
                    <div className="text-content pb-0">
                      <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap fw-500">
                        {t("dialogMaintenanceReports.irregularMaintenanceItem")}
                      </p>
                      {!!targetRow.irregularMaintenanceItemChoices.length
                        ? targetRow.irregularMaintenanceItemChoices.map(
                            (item: any, itemIndex: number) => (
                              <p
                                key={`irregular-${itemIndex}`}
                                className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap"
                              >
                                {item.irregularMaintenanceItemChoiceName}
                              </p>
                            ),
                          )
                        : "-"}
                    </div>

                    <div className="text-content">
                      <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap fw-500">
                        {t("dialogMaintenanceReports.maintenanceReason")}
                      </p>
                      <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
                        {`${
                          targetRow.maintenanceReasonChoice
                            .maintenanceReasonChoiceName
                        }　${
                          targetRow.maintenanceReasonPeriodChoice
                            ?.maintenanceReasonPeriodChoiceName || ""
                        }`}
                      </p>
                    </div>

                    {targetRow.comment && (
                      <div className="text-content" style={{ marginTop: -12 }}>
                        <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap fw-500">
                          {t("dialogReport.comment")}
                        </p>
                        <p
                          className={`txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap word-break ${
                            targetRow.machineReportMedias.length > 1 &&
                            targetRow.comment &&
                            "pt-0"
                          } `}
                        >
                          {targetRow.comment}
                        </p>
                      </div>
                    )}

                    {!!targetRow.machineReportRecordings?.length && (
                      <div
                        className="recordings-section"
                        style={{ marginTop: "12px", padding: "10px" }}
                      >
                        {targetRow.machineReportRecordings.map(
                          (recording: any, rIndex: number) => (
                            <div key={rIndex} style={{ marginBottom: "16px" }}>
                              <audio
                                controls
                                src={recording.recordingUrl}
                                style={{ width: "100%" }}
                              />
                            </div>
                          ),
                        )}
                      </div>
                    )}

                    <Swiper
                      cssMode={true}
                      navigation={true}
                      pagination={true}
                      mousewheel={true}
                      keyboard={true}
                      modules={[Navigation, Pagination, Mousewheel, Keyboard]}
                      className="swiper"
                    >
                      {targetRow.machineReportMedias.map(
                        (media: any, mediaIndex: number) => {
                          const isImage = media?.mimeType?.includes("image");
                          return (
                            <SwiperSlide key={`${media}_${mediaIndex}`}>
                              <div className="wrapper-file">
                                <div
                                  className="wrapper-file"
                                  onClick={() => {
                                    if (isImage) {
                                      setSelectedImage(media.mediaUrl);
                                      setViewerOpen(true);
                                    }
                                  }}
                                  style={{
                                    cursor: isImage ? "zoom-in" : "default",
                                  }}
                                >
                                  {renderMedia(media, mediaIndex)}
                                </div>
                              </div>

                              <p
                                className="created-at txt-gray-1 fs-12"
                                style={{
                                  textAlign: "right",
                                  marginRight: "10px",
                                  fontWeight: "bold",
                                }}
                              >
                                {media.captureMediaAt ? (
                                  isNaN(
                                    new Date(media.captureMediaAt).getTime(),
                                  ) ? (
                                    <span style={{ color: "red" }}></span>
                                  ) : (
                                    `${format(
                                      new Date(
                                        new Date(
                                          media.captureMediaAt,
                                        ).toLocaleString("en-US", {
                                          timeZone:
                                            Intl.DateTimeFormat().resolvedOptions()
                                              .timeZone,
                                        }),
                                      ),
                                      userTimeFormat,
                                    )}`
                                  )
                                ) : (
                                  ""
                                )}
                              </p>
                            </SwiperSlide>
                          );
                        },
                      )}
                    </Swiper>
                  </div>
                </div>
              )}

              {targetRow.reportVersion === "V2" &&
                targetRow.isPeriodicMaintenance && (
                  <div className="report-card">
                    <div className="content-report">
                      {targetRow.isPeriodicMaintenance != null && (
                        <div className="text-content pb-0">
                          <p className="txt-gray-1 fs-14 mt-0 mb-0 txt-pre-wrap fw-700">
                            {t(
                              "dialogMaintenanceReports.isPeriodicMaintenance",
                            )}
                          </p>
                        </div>
                      )}

                      <div className="text-content pb-0">
                        <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap fw-500">
                          {t("dialogMaintenanceReports.maintenanceInterval")}
                        </p>
                        {!!targetRow.intervalMaintenanceItemChoices.length
                          ? targetRow.intervalMaintenanceItemChoices.map(
                              (item: any, itemIndex: number) => (
                                <p
                                  key={`irregular-${itemIndex}`}
                                  className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap"
                                >
                                  {item.intervalMaintenanceItemChoiceName}
                                </p>
                              ),
                            )
                          : "-"}
                      </div>

                      <div className="text-content">
                        <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap fw-500">
                          {t("dialogMaintenanceReports.workScope")}
                        </p>
                        <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
                          {targetRow.maintenanceWorkScope || "-"}
                        </p>
                      </div>

                      {targetRow.comment && (
                        <div
                          className="text-content"
                          style={{ marginTop: -12 }}
                        >
                          <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap fw-500">
                            {t("dialogReport.comment")}
                          </p>
                          <p
                            className={`txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap word-break ${
                              targetRow.machineReportMedias.length > 1 &&
                              targetRow.comment &&
                              "pt-0"
                            } `}
                          >
                            {targetRow.comment}
                          </p>
                        </div>
                      )}

                      {!!targetRow.machineReportRecordings?.length && (
                        <div
                          className="recordings-section"
                          style={{ marginTop: "12px", padding: "10px" }}
                        >
                          {targetRow.machineReportRecordings.map(
                            (recording: any, rIndex: number) => (
                              <div
                                key={rIndex}
                                style={{ marginBottom: "16px" }}
                              >
                                <audio
                                  controls
                                  src={recording.recordingUrl}
                                  style={{ width: "100%" }}
                                />
                              </div>
                            ),
                          )}
                        </div>
                      )}

                      <Swiper
                        cssMode={true}
                        navigation={true}
                        pagination={true}
                        mousewheel={true}
                        keyboard={true}
                        modules={[Navigation, Pagination, Mousewheel, Keyboard]}
                        className="swiper"
                      >
                        {targetRow.machineReportMedias.map(
                          (media: any, mediaIndex: number) => {
                            const isImage = media?.mimeType?.includes("image");
                            return (
                              <SwiperSlide key={`${media}_${mediaIndex}`}>
                                <div className="wrapper-file">
                                  <div
                                    className="wrapper-file"
                                    onClick={() => {
                                      if (isImage) {
                                        setSelectedImage(media.mediaUrl);
                                        setViewerOpen(true);
                                      }
                                    }}
                                    style={{
                                      cursor: isImage ? "zoom-in" : "default",
                                    }}
                                  >
                                    {renderMedia(media, mediaIndex)}
                                  </div>
                                </div>

                                <p
                                  className="created-at txt-gray-1 fs-12"
                                  style={{
                                    textAlign: "right",
                                    marginRight: "10px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {media.captureMediaAt ? (
                                    isNaN(
                                      new Date(media.captureMediaAt).getTime(),
                                    ) ? (
                                      <span style={{ color: "red" }}></span>
                                    ) : (
                                      `${format(
                                        new Date(
                                          new Date(
                                            media.captureMediaAt,
                                          ).toLocaleString("en-US", {
                                            timeZone:
                                              Intl.DateTimeFormat().resolvedOptions()
                                                .timeZone,
                                          }),
                                        ),
                                        userTimeFormat,
                                      )}`
                                    )
                                  ) : (
                                    ""
                                  )}
                                </p>
                              </SwiperSlide>
                            );
                          },
                        )}
                      </Swiper>
                    </div>
                  </div>
                )}
              {targetRow.reportVersion === "V2" && (
                <>
                  {targetRow.items?.map((item: any, index: number) => (
                    <div key={`item-${index}`} className="report-card">
                      <div className="content-report">
                        {item.maintenanceItemOther &&
                        item.maintenanceItemOther.trim() !== "" ? (
                          <div className="text-content pb-0">
                            <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap fw-500">
                              {t("dialogMaintenanceReports.item")}
                            </p>
                            <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
                              {item.maintenanceItemOther}
                            </p>
                          </div>
                        ) : (
                          item.maintenanceItemName && (
                            <div className="text-content pb-0">
                              <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap fw-500">
                                {t("dialogMaintenanceReports.item")}
                              </p>
                              <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
                                {item.maintenanceItemName}
                              </p>
                            </div>
                          )
                        )}

                        {(item.isRefill || item.isReplace) && (
                          <>
                            {item.isRefill && (
                              <div className="text-content pb-0">
                                <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap fw-500">
                                  {t("dialogMaintenanceReports.action")}
                                </p>
                                <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
                                  {t("dialogMaintenanceReports.yesRefill")}
                                </p>
                              </div>
                            )}

                            {item.isReplace && (
                              <div className="text-content pb-0">
                                <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap fw-500">
                                  {t("dialogMaintenanceReports.action")}
                                </p>
                                <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
                                  {t("dialogMaintenanceReports.yesReplace")}
                                </p>
                              </div>
                            )}
                          </>
                        )}

                        {item.refillAmount != null && (
                          <div className="text-content pb-0">
                            <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap fw-500">
                              {t("dialogMaintenanceReports.refillAmount")}
                            </p>

                            <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
                              <span dir="ltr">
                                {formatNumberWithCommas(item.refillAmount)} L
                              </span>
                            </p>
                          </div>
                        )}

                        {item.comment && (
                          <div className="text-content">
                            <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap fw-500">
                              {t("dialogReport.comment")}
                            </p>
                            <p
                              className={`txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap word-break ${
                                item.comment && "pt-0"
                              } `}
                            >
                              {item.comment}
                            </p>
                          </div>
                        )}
                        {Array.isArray(item.itemMedias) &&
                          item.itemMedias.length > 0 && (
                            <Swiper
                              cssMode
                              navigation
                              pagination
                              mousewheel
                              keyboard
                              modules={[
                                Navigation,
                                Pagination,
                                Mousewheel,
                                Keyboard,
                              ]}
                              className="swiper"
                              style={{ marginTop: 2 }}
                            >
                              {item.itemMedias.map(
                                (media: any, mIndex: number) => {
                                  const isImage =
                                    media?.mimeType?.includes("image");
                                  const key =
                                    media?.machineReportMediaId ||
                                    `${mIndex}-${media?.fileName || "media"}`;

                                  const textTime =
                                    media?.captureMediaAt &&
                                    !isNaN(
                                      new Date(
                                        media.captureMediaAt as any,
                                      ).getTime(),
                                    )
                                      ? format(
                                          new Date(
                                            new Date(
                                              media.captureMediaAt,
                                            ).toLocaleString("en-US", {
                                              timeZone:
                                                Intl.DateTimeFormat().resolvedOptions()
                                                  .timeZone,
                                            }),
                                          ),
                                          userTimeFormat,
                                        )
                                      : "";

                                  return (
                                    <SwiperSlide key={key}>
                                      <div
                                        className="wrapper-file"
                                        onClick={() => {
                                          if (isImage) {
                                            setSelectedImage(media.mediaUrl);
                                            setViewerOpen(true);
                                          }
                                        }}
                                        style={{
                                          cursor: isImage
                                            ? "zoom-in"
                                            : "default",
                                        }}
                                      >
                                        {renderMedia(media, mIndex)}
                                      </div>

                                      {textTime && (
                                        <p
                                          className="created-at txt-gray-1 fs-12"
                                          style={{
                                            textAlign: "right",
                                            marginRight: 10,
                                            fontWeight: "bold",
                                          }}
                                        >
                                          {textTime}
                                        </p>
                                      )}
                                    </SwiperSlide>
                                  );
                                },
                              )}
                            </Swiper>
                          )}

                        {Array.isArray(item.itemRecordings) &&
                          item.itemRecordings.length > 0 && (
                            <div
                              className="recordings-section"
                              style={{ marginTop: 12, padding: 10 }}
                            >
                              {item.itemRecordings.map(
                                (rec: any, rIndex: number) => (
                                  <div
                                    key={
                                      rec?.machineReportRecordingId ||
                                      `rec-${rIndex}`
                                    }
                                    style={{ marginBottom: 16 }}
                                  >
                                    <audio
                                      controls
                                      src={rec.recordingUrl}
                                      style={{ width: "100%" }}
                                    />
                                  </div>
                                ),
                              )}
                            </div>
                          )}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )
        )}
      </DataDialog>

      <MaintenanceReportV3Dialog
        open={
          reportType === MAINTENANCE_REPORTS &&
          reportVersion !== "V1" &&
          reportVersion !== "V2"
        }
        report={
          targetRow?.reportVersion === "V3" ? (targetRow as Report) : null
        }
        loading={isLoadingMaintenance}
        groupId={id || ""}
        machineId={machineId}
        machineModelType={machineModelType}
        canEditOrDelete={canEditOrDeleteMaintReport}
        localeCode={localeCode}
        userTimeFormat={userTimeFormat}
        onClose={handleCloseDialog}
        onUpdateSuccess={() =>
          getMaintenanceReport(id, machineId, machineReportId)
        }
        onRequestPdfOutput={() => {
          setPendingDialog("MAINT");
          setPdfConfirmOpen(true);
        }}
      />

      <DataDialog
        disableBackdropClick={true}
        title={t("errNetwork.title")}
        open={openFormNoMic}
        setOpen={setOpenFormNoMic}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("noMic")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => setOpenFormNoMic(false)}
              className="btn btn-primary ml-8"
            >
              {t("ok")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        title={t("deleteRecording")}
        open={openFormDeleteRecording}
        setOpen={setOpenFormDeleteRecording}
        disableBackdropClick={true}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("inspectionDelete.content")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => setOpenFormDeleteRecording(false)}
              className="btn btn-no-border"
              style={{ marginTop: "4px" }}
            >
              {t("dialogReport.cancel")}
            </Button>

            <Button onClick={handleDeleteRecording} className="btn-delete">
              {t("formAction.delete")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        title={t("tableMachineDetail.location")}
        open={openLocation}
        setOpen={setOpenLocation}
        className="dialog-location-report"
        disableBackdropClick={true}
      >
        {record && (
          <>
            <iframe
              title="map-location"
              className="map-iframe"
              loading="lazy"
              src={`https://maps.google.com/maps?&q="+${record?.lat}, ${record?.lng}"&output=embed`}
            />
            <span className="fs-14 txt-gray-1 label-dialog">
              {t("tableMachineDetail.date")}
            </span>
            <p className="fs-16 txt-gray mt-4">
              {record?.reportedAt
                ? format(record?.reportedAt, userTimeFormat)
                : "-"}
              <br />
            </p>
            <span className="fs-14 txt-gray-1 label-dialog">
              {t("tableMachineDetail.reporter")}
            </span>
            <div className="flex-centered justify-content-start mt-8">
              {record?.userPictureUrl ? (
                record?.userPictureUrl === INVALID_URL ? (
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <IconInvalidUrl />
                  </Box>
                ) : (
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                    }}
                    alt="avatar-reporter"
                    src={record?.userPictureUrl}
                    imgProps={{
                      onError: () => {
                        setRecord({ ...record, userPictureUrl: INVALID_URL });
                      },
                    }}
                  />
                )
              ) : (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <IconUser />
                </Box>
              )}

              <span className="fs-16 txt-gray ml-5 word-break txt-pre-wrap">
                {record.reporterName
                  ? record.reporterName
                  : localeCode === "ja"
                    ? `${record?.surname} ${record?.givenName}`
                    : `${record?.givenName} ${record?.surname}`}
              </span>
            </div>
            <div className="flex justify-content-end">
              <Button
                onClick={() => setOpenLocation(false)}
                className="btn btn-no-border"
              >
                {t("dialogRefuel.close")}
              </Button>
            </div>
          </>
        )}
      </DataDialog>

      <ConfirmDialog
        open={openDialogConfirm}
        setOpen={setOpenDialogConfirm}
        {...confirmDialog}
      />

      {viewerOpen && (
        <CustomImageViewer
          imageUrl={selectedImage}
          onClose={() => setViewerOpen(false)}
        />
      )}

      {videoViewerOpen && selectedVideo && (
        <CustomVideoViewer
          videoUrl={selectedVideo}
          onClose={() => setVideoViewerOpen(false)}
        />
      )}
    </>
  );
}
